import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar as CalendarUI } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format, isValid, parseISO } from "date-fns";
import {
  Search, Filter, Download, RefreshCw, LogOut, MapPin, Package,
  Phone, Calendar, Loader2, X, ExternalLink, CheckSquare, Square, ArrowUpRight, ArrowRight,
  StickyNote, QrCode, Trash2, RotateCcw, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, ArrowLeft, CalendarDays, Store, Truck,
  MessageCircle, CheckCircle2, AlertCircle, PackageCheck, Link2, Link2Off, Share2, Database, ShieldCheck, Lock, Copy,
  Smartphone, Monitor, MonitorSmartphone, Tablet, Printer, FileImage, Clock, ClipboardList,
} from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import { TANISH_GOOGLE_REVIEW_URL } from "@/lib/googleReview";
import { TANISH_WEBSITE_AI_REVIEW_URL } from "@/lib/websiteReview";
import Logo from "@/components/portal/Logo";
import PosterModal from "@/components/portal/PosterModal";
import {
  formatRelativeTime, rowChargeRupees, rowChargeLabel,
  STATUS_COLORS, STATUSES, SORT_GROUPS, SORT_LABELS, STATUS_RANK,
  todayIso, SAVED_VIEWS,
  SHARE_SLOT_LABELS, buildInvoiceShareText, shareInvoice, shareInvoicesBulk, searchRank,
  buildCustomerWhatsAppMessage,
  WA_BIZ_ACK_KEY, isWaBizAcknowledged, markWaBizAcknowledged,
  whatsappDeepLink, copyMobileDigits, buildWorkListMessage, whatsappShareLink,
  NOTIF_LABELS, PICKUP_SLOT_LABELS, formatSlot, SALESMEN,
  buildBulkWhatsAppMessage, hasValidMobile,
  statusChangeLine,
} from "./admin/utils";
import {
  NotificationBadges, AttentionMarks, Info, MobileVerificationBadge,
  DateFilterField, SalesmanCell, DeliveryDateCell, SortMenu,
} from "./admin/components";
import DetailDrawer from "./admin/DetailDrawer";
import SessionsModals from "./admin/SessionsModals";
import { preparePrint } from "./admin/printInvoices";
import PrintConfirmModal from "./admin/PrintConfirmModal";
import BulkWhatsAppModal from "./admin/BulkWhatsAppModal";
import ConfirmDialogs from "./admin/ConfirmDialogs";
import PreRegisterModals from "./admin/PreRegisterModals";
import ReviewLibraryPanel from "./admin/ReviewLibraryPanel";
import { DeliveryCalendar } from "./admin/DeliveryCalendar";
import PreparationRegister from "./admin/PreparationRegister";
import OtpVerificationSetting, { OtpDisabledBanner, useOtpSetting } from "./admin/OtpVerificationSetting";


/** Copy the 10-digit Indian mobile number (no +91, no spaces, no
 * hyphens) from a submission row to the clipboard. Uses the async
 * Clipboard API with a `document.execCommand('copy')` fallback for
 * older browsers / non-secure contexts. Shows a subtle toast; no
 * modal, no confirmation. */
/**
 * Bulk WhatsApp helpers now live in `./admin/utils` (see
 * buildBulkWhatsAppMessage, hasValidMobile, firstName,
 * bulkDeliveryPhrase). They were moved during the Feb-2026 refactor so
 * the modal component and this file share one canonical implementation.
 */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");          // applied search (drives load())
  const otpSetting = useOtpSetting();
  const [qDraft, setQDraft] = useState(""); // what the user is typing — never triggers a search
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [me, setMe] = useState({ email: "", role: "", group: "" });
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("Ready for Dispatch");
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk WhatsApp state — three phases:
  //   "composer" → staff enters the delivery update + reviews preview
  //   "queue"    → sequential per-customer WhatsApp queue
  //   "summary"  → final Sent / Skipped tally
  // `bulkWaOpen === null` = closed.
  const [bulkWaOpen, setBulkWaOpen] = useState(null);          // null | "composer" | "queue" | "summary"
  const [bulkWaUpdate, setBulkWaUpdate] = useState("");        // free-text delivery update, e.g. "Monday"
  const [bulkWaQueue, setBulkWaQueue] = useState([]);          // frozen list of {row} snapshots at start
  const [bulkWaIdx, setBulkWaIdx] = useState(0);
  const [bulkWaSent, setBulkWaSent] = useState([]);            // invoice_numbers marked Sent
  const [bulkWaSkipped, setBulkWaSkipped] = useState([]);      // invoice_numbers marked Skipped
  const [bulkWaPreviewIdx, setBulkWaPreviewIdx] = useState(0); // which selected row's preview to show
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [reviewQrUrl, setReviewQrUrl] = useState("");
  const [reviewQrError, setReviewQrError] = useState(false);
  const [websiteReviewQrUrl, setWebsiteReviewQrUrl] = useState("");
  const [websiteReviewQrError, setWebsiteReviewQrError] = useState(false);
  const [qrTab, setQrTab] = useState("store");
  const [posterKind, setPosterKind] = useState("store");
  const [view, setView] = useState("active"); // 'active' | 'delivered' | 'calendar' | 'trash'
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [trashRows, setTrashRows] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // submission being deleted
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [emptyConfirmText, setEmptyConfirmText] = useState("");
  // Per-invoice permanent-delete confirmation. `null` = closed; a row = shown.
  // Kept separate from `confirmEmpty` so the two flows never share state.
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [deliveryFilter, setDeliveryFilter] = useState("all"); // all | unassigned | today | tomorrow | week | date
  const [deliveryCustomDate, setDeliveryCustomDate] = useState("");
  const [sort, setSort] = useState(""); // "" | delivery_asc | delivery_desc | pickup_asc | pickup_desc
  const [fulfillment, setFulfillment] = useState("all"); // all | home_delivery | store_pickup
  const [pickupDateFilter, setPickupDateFilter] = useState("");
  // Collapsed-by-default filter + sort UX (see rework request). Keeps every
  // existing control intact — only the *visibility* is now user-driven.
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [posterModalOpen, setPosterModalOpen] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  // Active Sessions modal — populated by GET /api/admin/sessions on open.
  // `sessionsList` is null before the first fetch, [] when empty.
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionsList, setSessionsList] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsBusy, setSessionsBusy] = useState(""); // id being revoked, or "all"
  const [confirmRevokeSession, setConfirmRevokeSession] = useState(null); // session row
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  // Logout confirmation — one-tap sign-out was too easy to trigger by
  // mistake. Staff now confirm through a modal before the token is
  // cleared. `logoutBusy` prevents double-taps while the redirect runs.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  // Cross-group invoice linking — modal state + unlink confirmation.
  // `linkRow` is the row being edited; `unlinkTarget` is { row, link } awaiting confirmation.
  const [linkRow, setLinkRow] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  // Business-only WhatsApp gate — stores the pending `() => void` while
  // we wait for the staff member to acknowledge the "Business account only"
  // reminder. `null` means either already acknowledged (localStorage flag)
  // or no pending action.
  const [waBizGate, setWaBizGate] = useState(null);

  const guardedOpenWhatsApp = React.useCallback((fn) => {
    if (!fn) return;
    if (isWaBizAcknowledged()) { fn(); return; }
    setWaBizGate(() => fn);
  }, []);

  const confirmWaBizGate = () => {
    markWaBizAcknowledged();
    const fn = waBizGate;
    setWaBizGate(null);
    if (typeof fn === "function") fn();
  };

  // WhatsApp customer-notify — the row being reviewed inside the confirm modal.
  const [waRow, setWaRow] = useState(null);
  // Pre-Register + Awaiting-Customer state.
  const [preRegOpen, setPreRegOpen] = useState(false);
  // `home_delivery_amount_rupees` is a raw string so an empty box shows
  // an empty box (not "0"), and staff-entered values like "1500" round-
  // trip cleanly. Converted to paise (× 100) at submit-time. Only used
  // when `home_delivery_chargeable=true`.
  const [preRegForm, setPreRegForm] = useState({
    invoice_number: "",
    internal_delivery_date: "",
    salesman: "",
    home_delivery_chargeable: false,
    home_delivery_amount_rupees: "",
  });
  const [preRegDateOpen, setPreRegDateOpen] = useState(false);
  const [preRegBusy, setPreRegBusy] = useState(false);
  const [awaitingOpen, setAwaitingOpen] = useState(false);
  const [awaitingRows, setAwaitingRows] = useState([]);

  const loadAwaiting = async () => {
    try {
      const { data } = await api.get("/admin/awaiting-customer");
      setAwaitingRows(data.rows || []);
    } catch (e) { toast.error(formatError(e)); }
  };

  const submitPreReg = async () => {
    setPreRegBusy(true);
    try {
      // Client-side amount validation — the backend re-validates so a
      // tampered request still fails at the server, but early-catching
      // an empty/zero/negative value here keeps the UX responsive.
      let amount_paise = null;
      if (isBridal && preRegForm.home_delivery_chargeable) {
        const raw = String(preRegForm.home_delivery_amount_rupees || "").trim();
        const rupees = Number(raw);
        if (!raw || !Number.isFinite(rupees) || rupees <= 0) {
          toast.error("Please enter a Payment Amount greater than ₹0.");
          setPreRegBusy(false);
          return;
        }
        // Round to whole paise so ₹15.50 → 1550p exactly. Banker's
        // rounding avoided — .5 always rounds up to match how staff
        // will read "₹15.50".
        amount_paise = Math.round(rupees * 100);
        if (amount_paise > 10_000_000) {
          toast.error("Payment Amount cannot exceed ₹1,00,000.");
          setPreRegBusy(false);
          return;
        }
      }
      const body = {
        invoice_number: preRegForm.invoice_number.trim(),
        internal_delivery_date: preRegForm.internal_delivery_date || null,
        salesman: isBridal ? (preRegForm.salesman || "") : null,
        home_delivery_chargeable: isBridal ? !!preRegForm.home_delivery_chargeable : null,
        home_delivery_amount_paise: amount_paise,
      };
      await api.post("/admin/pre-register", body);
      const rupeeStr = amount_paise
        ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount_paise / 100)
        : "";
      toast.success(`Pre-registered ${body.invoice_number}${rupeeStr ? ` · ${rupeeStr} Home Delivery` : ""}`);
      setPreRegOpen(false);
      setPreRegForm({ invoice_number: "", internal_delivery_date: "", salesman: "", home_delivery_chargeable: false, home_delivery_amount_rupees: "" });
      loadAwaiting();
    } catch (e) { toast.error(formatError(e)); }
    finally { setPreRegBusy(false); }
  };

  const cancelPreReg = async (id) => {
    if (!window.confirm("Cancel this pre-registration?")) return;
    try {
      await api.delete(`/admin/pre-register/${id}`);
      setAwaitingRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Pre-registration cancelled");
    } catch (e) { toast.error(formatError(e)); }
  };

  // Toggle the invoice-level "Charge for Home Delivery" flag. Server
  // enforces Bridal-only + role scoping; we optimistically flip and
  // roll back on failure. When enabling for the first time (no amount
  // stored yet) we prompt the staff for the custom amount so no BRIDAL
  // invoice ever silently defaults to ₹300.
  const toggleChargeable = async (row, next) => {
    const val = !!next;
    if (row?.payment?.status === "paid") {
      toast.error(`Home delivery charge for ${row.invoice_number} is locked — ₹${Math.round((row.payment.amount || 0) / 100)} was paid online.`);
      return;
    }
    let amount_paise = row?.home_delivery_amount_paise || 0;
    if (val && !amount_paise) {
      // Ask staff for the invoice-specific amount. Legacy or newly-
      // flipped rows arrive here without a stored amount.
      const raw = window.prompt(
        `Home Delivery charge for ${row.invoice_number}\nEnter amount in ₹ (e.g. 800):`,
        ""
      );
      if (raw == null) return; // cancelled
      const rupees = Number(String(raw).trim());
      if (!Number.isFinite(rupees) || rupees <= 0) {
        toast.error("Please enter a valid amount greater than ₹0.");
        return;
      }
      if (rupees > 100000) {
        toast.error("Amount cannot exceed ₹1,00,000.");
        return;
      }
      amount_paise = Math.round(rupees * 100);
    }
    const optimistic = { home_delivery_chargeable: val, home_delivery_amount_paise: val ? amount_paise : 0 };
    setAwaitingRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...optimistic } : x)));
    setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...optimistic } : x)));
    setDetail((d) => (d && d.id === row.id ? { ...d, ...optimistic } : d));
    try {
      const body = { home_delivery_chargeable: val };
      if (val) body.home_delivery_amount_paise = amount_paise;
      const { data } = await api.patch(`/admin/submissions/${row.id}/chargeable`, body);
      // Authoritative echo — never trust the local optimistic value once
      // the server has replied.
      const server = {
        home_delivery_chargeable: !!data?.home_delivery_chargeable,
        home_delivery_amount_paise: Number(data?.home_delivery_amount_paise) || 0,
      };
      setAwaitingRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...server } : x)));
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...server } : x)));
      setDetail((d) => (d && d.id === row.id ? { ...d, ...server } : d));
      const label = server.home_delivery_amount_paise
        ? `₹${Math.round(server.home_delivery_amount_paise / 100).toLocaleString("en-IN")}`
        : "";
      toast.success(val ? `${label} Home Delivery enabled for ${row.invoice_number}` : `Free Home Delivery restored for ${row.invoice_number}`);
    } catch (e) {
      const revert = { home_delivery_chargeable: !val, home_delivery_amount_paise: !val ? (row?.home_delivery_amount_paise || 0) : 0 };
      setAwaitingRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...revert } : x)));
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...revert } : x)));
      setDetail((d) => (d && d.id === row.id ? { ...d, ...revert } : d));
      toast.error(formatError(e));
    }
  };

  // Backup admin panel — only visible to allow-listed owner emails.
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const isBackupAdmin = !!me?.email && !!backupStatus;
  const isBridal = me?.role === "staff_group_b";

  const loadBackupStatus = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backups/status");
      setBackupStatus(data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(formatError(e));
      setBackupStatus(null);   // hide the panel for non-owner accounts
    }
  }, []);

  // Only poll the backup endpoint when the signed-in user is on the
  // BACKUP_ADMIN_EMAILS allowlist (surfaced via /admin/me). This avoids
  // a needless 403 on every dashboard load for regular staff and keeps
  // the DevTools console clean.
  useEffect(() => {
    if (me?.backup_admin) loadBackupStatus();
    else setBackupStatus(null);
  }, [me?.backup_admin, loadBackupStatus]);

  const runBackupNow = async () => {
    setBackupBusy(true);
    try {
      await api.post("/admin/backups/run");
      toast.success("Backup started — refreshing status…");
      // Poll status a few times for the background task to complete.
      for (let i = 0; i < 5; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        await loadBackupStatus();
        if (backupStatus?.latest_run?.status === "success") break;
      }
      await loadBackupStatus();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBackupBusy(false);
    }
  };

  const email = me?.email || "admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (deliveryFilter && deliveryFilter !== "all") params.delivery_filter = deliveryFilter;
      if (deliveryFilter === "date" && deliveryCustomDate) params.delivery_date = deliveryCustomDate;
      // View: 'active' hides Delivered, 'delivered' shows only Delivered.
      params.view = view === "delivered" ? "delivered" : "active";
      // Note: sort is applied client-side (see sortedRows below) so it composes freely with any filter.
      if (fulfillment && fulfillment !== "all") params.fulfillment = fulfillment;
      if (pickupDateFilter) params.pickup_date = pickupDateFilter;
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/submissions", { params }),
        api.get("/admin/stats"),
      ]);
      setRows(list);
      setStats(s);
      setSelected(new Set());
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Load identity + group once so the dashboard header can show which role
  // is signed in. Purely presentational — every real authz decision is on
  // the backend based on the JWT `role` claim.
  useEffect(() => {
    api.get("/admin/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/website-review-qr/config")
      .then(({ data }) => {
        if (data?.url === TANISH_WEBSITE_AI_REVIEW_URL) {
          setWebsiteReviewQrUrl(data.url);
          return;
        }
        setWebsiteReviewQrError(true);
      })
      .catch(() => setWebsiteReviewQrError(true));
  }, []);
  // Auto-reload when any server-side filter changes or the view (Active/Delivered) toggles.
  // `status` is here so tests like: Search=2058, Status=All → flip Status=Delivered
  // update live. `q` is the APPLIED search: it only changes on explicit submit/clear/preset.
  useEffect(() => { if (rows.length || deliveryFilter !== "all" || fulfillment !== "all" || pickupDateFilter || view === "delivered" || status !== "all") load(); /* eslint-disable-next-line */ }, [deliveryFilter, deliveryCustomDate, fulfillment, pickupDateFilter, view, status]);
  const qMounted = useRef(false);
  useEffect(() => {
    if (!qMounted.current) { qMounted.current = true; return; }
    load();
    // eslint-disable-next-line
  }, [q]);
  const submitSearch = (e) => {
    e?.preventDefault?.();
    const next = qDraft.trim();
    if (next === q) { load(); return; }
    setQ(next);
  };
  const clearSearch = () => { setQDraft(""); if (q) setQ(""); };

  const updateDeliveryDate = async (row, newDate) => {
    const prev = row.internal_delivery_date || "";
    // optimistic update
    setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, internal_delivery_date: newDate || null } : x)));
    try {
      await api.patch(`/admin/submissions/${row.id}/delivery-date`, { date: newDate || null });
      setCalendarRefreshKey((key) => key + 1);
      toast.success(newDate ? `Delivery date set for ${row.invoice_number}` : `Delivery date cleared for ${row.invoice_number}`);
    } catch (e) {
      // revert
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, internal_delivery_date: prev || null } : x)));
      toast.error(formatError(e));
    }
  };

  const updateSalesman = async (row, salesman) => {
    const prev = row.salesman || "";
    const next = salesman || "";
    // Optimistic update — reverts on server rejection so an invalid value
    // never appears to succeed. Server validates against ALLOWED_SALESMEN.
    setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, salesman: next } : x)));
    try {
      await api.patch(`/admin/submissions/${row.id}/salesman`, { salesman: next });
      toast.success(next ? `Assigned to ${next}` : "Salesman cleared");
    } catch (e) {
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, salesman: prev } : x)));
      toast.error(formatError(e));
    }
  };

  const cycleSort = () => {
    setSort((s) => (s === "" ? "delivery_asc" : s === "delivery_asc" ? "delivery_desc" : ""));
  };

  useEffect(() => {
    api.get("/config/qr").then(({ data }) => setQrUrl(data.url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/review-qr/config")
      .then(({ data }) => {
        if (data?.url === TANISH_GOOGLE_REVIEW_URL) {
          setReviewQrUrl(data.url);
          return;
        }
        setReviewQrError(true);
      })
      .catch(() => setReviewQrError(true));
  }, []);

  // ---- Saved Filter Views ------------------------------------------------
  // Baseline filter state (view=active). Applying a chip resets every
  // filter field to this baseline first, then layers on the preset.
  // Doing it this way means a chip is a pure snapshot — there's no
  // leaking of unrelated filters between chips.
  const FILTER_BASELINE = {
    q: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    fulfillment: "all",
    pickupDateFilter: "",
    deliveryFilter: "all",
    deliveryCustomDate: "",
  };
  const applyView = (v) => {
    const preset = { ...FILTER_BASELINE, ...v.preset() };
    setQ(preset.q);
    setQDraft(preset.q);
    setStatus(preset.status);
    setDateFrom(preset.dateFrom);
    setDateTo(preset.dateTo);
    setFulfillment(preset.fulfillment);
    setPickupDateFilter(preset.pickupDateFilter);
    setDeliveryFilter(preset.deliveryFilter);
    setDeliveryCustomDate(preset.deliveryCustomDate);
    setView("active");
    // Collapse the filter drawer so the chip result is visible immediately.
    setFilterPanelOpen(false);
  };
  const matchesView = (v) => {
    const preset = { ...FILTER_BASELINE, ...v.preset() };
    return (
      view === "active" &&
      (q || "") === preset.q &&
      status === preset.status &&
      (dateFrom || "") === preset.dateFrom &&
      (dateTo || "") === preset.dateTo &&
      fulfillment === preset.fulfillment &&
      (pickupDateFilter || "") === preset.pickupDateFilter &&
      deliveryFilter === preset.deliveryFilter
    );
  };
  const isDefaultView =
    view === "active" &&
    (q || "") === "" &&
    status === "all" &&
    (dateFrom || "") === "" &&
    (dateTo || "") === "" &&
    fulfillment === "all" &&
    (pickupDateFilter || "") === "" &&
    deliveryFilter === "all";
  const clearAllFilters = () => {
    setQ("");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
    setFulfillment("all");
    setPickupDateFilter("");
    setDeliveryFilter("all");
    setDeliveryCustomDate("");
  };

  const downloadQr = async (format, kind = "store") => {
    try {
      const isReview = kind === "review";
      const isWebsiteReview = kind === "website";
      const baseUrl = isReview
        ? `${API}/admin/review-qr`
        : isWebsiteReview
          ? `${API}/admin/website-review-qr`
          : `${API}/qr`;
      const res = await fetch(
        `${baseUrl}?format=${format}&size=${format === "png" ? 30 : 20}`,
        isReview || isWebsiteReview ? { credentials: "include" } : undefined,
      );
      if (!res.ok) throw new Error("QR download failed");
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = isReview
        ? `tanish-textiles-google-review-qr.${format}`
        : isWebsiteReview
          ? `tanish-textiles-website-ai-review-qr.${format}`
          : `tanish-textiles-qr.${format}`;
      a.click();
      window.URL.revokeObjectURL(href);
      const label = isReview
        ? "Google Review QR"
        : isWebsiteReview
          ? "Website AI Review QR"
          : "Store QR";
      toast.success(`${label} downloaded (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const isWebsiteReviewQr = qrTab === "website";
  const isDirectReviewQr = qrTab === "review";
  const activeQrUrl = isWebsiteReviewQr
    ? websiteReviewQrUrl
    : isDirectReviewQr
      ? reviewQrUrl
      : qrUrl;
  const activeQrEndpoint = isWebsiteReviewQr
    ? `${API}/admin/website-review-qr`
    : isDirectReviewQr
      ? `${API}/admin/review-qr`
      : `${API}/qr`;
  const activeQrReady = Boolean(activeQrUrl);
  const activeQrError = isWebsiteReviewQr
    ? websiteReviewQrError
    : isDirectReviewQr
      ? reviewQrError
      : false;

  useEffect(() => {
    if (!detail?.id) {
      setNotes([]);
      setNoteText("");
      return;
    }
    let cancelled = false;
    api.get(`/admin/submissions/${detail.id}/notes`)
      .then(({ data }) => { if (!cancelled) setNotes(data); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [detail?.id]);

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || !detail?.id) return;
    setNoteBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${detail.id}/notes`, { text });
      setNotes((n) => [data, ...n]);
      setRows((r) => r.map((x) => (x.id === detail.id ? { ...x, notes_count: (x.notes_count || 0) + 1, internal_notes: [data.text, ...(x.internal_notes || [])] } : x)));
      setNoteText("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setNoteBusy(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const row = rows.find((x) => x.id === id);
    if (
      newStatus !== "Pending"
      && row?.mobile_verification === "not_verified"
      && !window.confirm(
        "This customer skipped mobile verification. Have you confirmed their details by phone before dispatch?"
      )
    ) {
      return false;
    }
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status: newStatus });
      // In Active view: an item that just became Delivered moves to the Delivered archive.
      // In Delivered view: an item that was reverted from Delivered moves back to Active.
      const leavesCurrentView =
        (view === "active" && newStatus === "Delivered") ||
        (view === "delivered" && newStatus !== "Delivered");
      if (leavesCurrentView) {
        setRows((r) => r.filter((x) => x.id !== id));
        setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
        if (detail?.id === id) setDetail(null);
        toast.success(newStatus === "Delivered" ? "Moved to Delivered Invoices" : "Moved back to Active Submissions");
      } else {
        setRows((r) => r.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
        toast.success("Status updated");
      }
      // refresh stats
      const { data: s } = await api.get("/admin/stats");
      setStats(s);
      setCalendarRefreshKey((key) => key + 1);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const openCalendarInvoice = async (delivery) => {
    try {
      const { data } = await api.get("/admin/submissions", {
        params: {
          q: delivery.invoice_number,
          view: "active",
          limit: 50,
        },
      });
      const current = data.find((row) => row.id === delivery.id);
      if (!current) {
        toast.error("This invoice is no longer available in Active Submissions.");
        return;
      }
      setDetail(current);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const openRegisterSubmission = async (entry) => {
    try {
      const { data } = await api.get("/admin/submissions", {
        params: { q: entry.invoice_number, view: entry.submission?.status === "Delivered" ? "delivered" : "active", limit: 50 },
      });
      const current = data.find((row) => row.id === entry.submission?.id) || data.find((row) => row.invoice_number === entry.invoice_number);
      if (!current) {
        toast.error("The linked website submission is not available in your lists.");
        return;
      }
      setDetail(current);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === sortedRows.length && sortedRows.length > 0) return new Set();
      return new Set(sortedRows.map((r) => r.id));
    });
  };

  const shareSelectedWorkList = () => {
    if (selected.size === 0) return;
    const chosen = sortedRows.filter((r) => selected.has(r.id));
    const text = buildWorkListMessage(chosen);
    const a = document.createElement("a");
    a.href = whatsappShareLink(text);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Work list for ${chosen.length} invoice${chosen.length === 1 ? "" : "s"} opened in WhatsApp. Selection kept.`);
  };

  const bulkUpdate = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const prevMap = {};
    for (const r of rows) if (selected.has(r.id)) prevMap[r.id] = r.status;
    try {
      const { data } = await api.patch("/admin/submissions/bulk-status", { ids, status: bulkStatus });
      const leavesCurrentView =
        (view === "active" && bulkStatus === "Delivered") ||
        (view === "delivered" && bulkStatus !== "Delivered");
      if (leavesCurrentView) {
        setRows((r) => r.filter((x) => !selected.has(x.id)));
      } else {
        setRows((r) => r.map((x) => (selected.has(x.id) ? { ...x, status: bulkStatus } : x)));
      }
      const { data: s } = await api.get("/admin/stats");
      setStats(s);
      setCalendarRefreshKey((key) => key + 1);
      setSelected(new Set());
      toast.success(
        `${data.modified} order${data.modified === 1 ? "" : "s"} moved to "${bulkStatus}"`,
        {
          duration: 8000,
          action: {
            label: "Undo",
            onClick: () => undoBulk(prevMap),
          },
        }
      );
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const undoBulk = async (prevMap) => {
    const groups = {};
    for (const [id, st] of Object.entries(prevMap)) {
      if (!groups[st]) groups[st] = [];
      groups[st].push(id);
    }
    try {
      await Promise.all(
        Object.entries(groups).map(([st, ids]) =>
          api.patch("/admin/submissions/bulk-status", { ids, status: st })
        )
      );
      // Reload so items that moved between Active/Delivered come back correctly.
      await load();
      toast.success("Reverted to previous status");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // ---------- Bulk WhatsApp ----------
  // Freeze the current selection into an ordered queue so subsequent
  // filter changes or list refreshes cannot corrupt the pending sends.
  // Restricted to the Active view per spec; group-isolation is
  // already guaranteed because `rows` only holds this admin's group.
  const openBulkWhatsApp = () => {
    if (selected.size === 0) return;
    if (view !== "active") {
      toast.error("Bulk WhatsApp is only available on Active Submissions.");
      return;
    }
    // Preserve on-screen order (sorted rows) so preview navigation
    // matches what the staff was just looking at.
    const queue = sortedRows.filter((r) => selected.has(r.id)).map((r) => ({ ...r }));
    if (queue.length === 0) return;
    setBulkWaQueue(queue);
    setBulkWaUpdate("");
    setBulkWaIdx(0);
    setBulkWaPreviewIdx(0);
    setBulkWaSent([]);
    setBulkWaSkipped([]);
    setBulkWaOpen("composer");
  };

  const closeBulkWa = () => {
    setBulkWaOpen(null);
    setBulkWaQueue([]);
    setBulkWaUpdate("");
    setBulkWaIdx(0);
    setBulkWaPreviewIdx(0);
    setBulkWaSent([]);
    setBulkWaSkipped([]);
  };

  const startBulkWaQueue = () => {
    if (!bulkWaUpdate.trim()) {
      toast.error("Please enter a delivery update (e.g. Monday, Tomorrow).");
      return;
    }
    setBulkWaIdx(0);
    setBulkWaOpen("queue");
  };

  const advanceBulkWa = () => {
    const nextIdx = bulkWaIdx + 1;
    if (nextIdx >= bulkWaQueue.length) {
      setBulkWaOpen("summary");
    } else {
      setBulkWaIdx(nextIdx);
    }
  };

  const bulkWaMarkSent = () => {
    const cur = bulkWaQueue[bulkWaIdx];
    if (!cur) return;
    setBulkWaSent((s) => [...s, cur.invoice_number]);
    advanceBulkWa();
  };

  const bulkWaMarkSkipped = () => {
    const cur = bulkWaQueue[bulkWaIdx];
    if (!cur) return;
    setBulkWaSkipped((s) => [...s, cur.invoice_number]);
    advanceBulkWa();
  };

  const bulkWaOpenChat = () => {
    const cur = bulkWaQueue[bulkWaIdx];
    if (!cur || !hasValidMobile(cur)) return;
    const msg = buildBulkWhatsAppMessage(cur, bulkWaUpdate);
    const url = whatsappDeepLink(cur, msg);
    guardedOpenWhatsApp(() => window.open(url, "_blank", "noopener,noreferrer"));
  };

  const exportFile = async (format) => {
    try {
      // Cookie-based auth — the browser attaches the HttpOnly session
      // cookie automatically when `credentials: 'include'` is set.
      const res = await fetch(`${API}/admin/export?format=${format}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tanish-deliveries.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const logout = async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    // Revoke the server-side session so a refresh cannot restore it.
    // We ignore network errors here — the cookie clear on 200 is the
    // ideal path, but even if the request fails the redirect below
    // still lands the user on the login screen and the session will
    // TTL-evict on the server.
    try { await api.post("/admin/logout"); } catch { /* noop */ }
    navigate("/admin/login", { replace: true });
  };

  // ---------- Active Sessions ----------
  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const { data } = await api.get("/admin/sessions");
      // New shape: {groups: [{panel, email, role, sessions:[]}, ...]}.
      // Fall back to old shape {sessions:[]} defensively during rollout.
      if (Array.isArray(data?.groups)) {
        setSessionsList(data.groups);
      } else if (Array.isArray(data?.sessions)) {
        setSessionsList([{ panel: "", email: me?.email || "", role: me?.role || "", sessions: data.sessions }]);
      } else {
        setSessionsList([]);
      }
    } catch (e) {
      toast.error(formatError(e));
      setSessionsList([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const openSessions = () => {
    setSessionsOpen(true);
    loadSessions();
  };

  const revokeSession = async (session) => {
    if (!session?.id) return;
    setSessionsBusy(session.id);
    try {
      await api.delete(`/admin/sessions/${session.id}`);
      toast.success("Device signed out");
      setConfirmRevokeSession(null);
      await loadSessions();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSessionsBusy("");
    }
  };

  const revokeAllSessions = async () => {
    setSessionsBusy("all");
    try {
      await api.post("/admin/sessions/revoke-all");
      // The revoke-all endpoint clears our own cookie server-side too,
      // so any further request will 401. Land on /admin/login.
      toast.success("Signed out of all devices");
      navigate("/admin/login", { replace: true });
    } catch (e) {
      toast.error(formatError(e));
      setSessionsBusy("");
    }
  };

  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get("/admin/recycle-bin");
      setTrashRows(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error(formatError(e));
    } finally {
      setTrashLoading(false);
    }
  };

  useEffect(() => {
    if (view === "trash") loadTrash();
    // eslint-disable-next-line
  }, [view]);

  const deleteSubmission = async (sub) => {
    try {
      await api.delete(`/admin/submissions/${sub.id}`);
      setRows((r) => r.filter((x) => x.id !== sub.id));
      setSelected((s) => { const n = new Set(s); n.delete(sub.id); return n; });
      const { data: st } = await api.get("/admin/stats");
      setStats(st);
      toast.success(`Invoice ${sub.invoice_number} moved to Recycle Bin`);
      setConfirmDelete(null);
      if (detail?.id === sub.id) setDetail(null);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const restoreSubmission = async (sub) => {
    try {
      await api.post(`/admin/submissions/${sub.id}/restore`);
      setTrashRows((r) => r.filter((x) => x.id !== sub.id));
      toast.success(`Invoice ${sub.invoice_number} restored`);
      const { data: st } = await api.get("/admin/stats");
      setStats(st);
    } catch (e) {
      const msg = e?.response?.data?.detail || formatError(e);
      toast.error(msg, { duration: 8000 });
    }
  };

  const permanentlyDelete = async (sub) => {
    if (!sub) return;
    setPurgeBusy(true);
    try {
      await api.delete(`/admin/recycle-bin/${sub.id}`);
      setTrashRows((r) => r.filter((x) => x.id !== sub.id));
      // Also strip this invoice from any linked_invoices badges shown on
      // currently-loaded active rows so the UI matches the backend.
      setRows((r) => r.map((x) => ({
        ...x,
        linked_invoices: (x.linked_invoices || []).filter((l) => l.invoice_number !== sub.invoice_number),
      })));
      toast.success(`Invoice ${sub.invoice_number} permanently deleted`);
      setConfirmPurge(null);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setPurgeBusy(false);
    }
  };

  const emptyRecycleBin = async () => {
    try {
      const { data } = await api.delete("/admin/recycle-bin");
      setTrashRows([]);
      setConfirmEmpty(false);
      setEmptyConfirmText("");
      toast.success(`Permanently deleted ${data.deleted} submission${data.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // Cross-group invoice link — after a successful POST, patch BOTH the
  // source row (open in the modal) and the target row (if the target
  // happens to also be on-screen — same-group staff wouldn't see it,
  // but the master admin might). Refreshing lets the badge appear
  // instantly without a full reload.
  const applyLinkLocally = (srcRowId, srcInvoice, targetPreview) => {
    const upsertLink = (arr, newLink) => {
      const existing = Array.isArray(arr) ? arr : [];
      if (existing.some((l) => l.invoice_number === newLink.invoice_number)) return existing;
      return [...existing, newLink];
    };
    const srcGroup = /^1\d{3}$/.test(srcInvoice) ? "A" : "B";
    const srcLink = { invoice_number: srcInvoice, group: srcGroup, group_label: srcGroup === "A" ? "Sider" : "Bridal" };
    const tgtLink = { invoice_number: targetPreview.invoice_number, group: targetPreview.group, group_label: targetPreview.group_label };

    setRows((rs) => rs.map((x) => {
      if (x.id === srcRowId)                       return { ...x, linked_invoices: upsertLink(x.linked_invoices, tgtLink) };
      if (x.invoice_number === tgtLink.invoice_number) return { ...x, linked_invoices: upsertLink(x.linked_invoices, srcLink) };
      return x;
    }));
    // Also patch the detail drawer if it's currently viewing the source row.
    setDetail((d) => (d && d.id === srcRowId ? { ...d, linked_invoices: upsertLink(d.linked_invoices, tgtLink) } : d));
  };

  const removeLinkLocally = (srcRowId, srcInvoice, targetInvoice) => {
    const pull = (arr, inv) => (arr || []).filter((l) => l.invoice_number !== inv);
    setRows((rs) => rs.map((x) => {
      if (x.id === srcRowId)                     return { ...x, linked_invoices: pull(x.linked_invoices, targetInvoice) };
      if (x.invoice_number === targetInvoice)    return { ...x, linked_invoices: pull(x.linked_invoices, srcInvoice) };
      return x;
    }));
    setDetail((d) => {
      if (!d) return d;
      if (d.id === srcRowId)                     return { ...d, linked_invoices: pull(d.linked_invoices, targetInvoice) };
      if (d.invoice_number === targetInvoice)    return { ...d, linked_invoices: pull(d.linked_invoices, srcInvoice) };
      return d;
    });
  };

  const unlinkInvoice = async () => {
    if (!unlinkTarget) return;
    const { row, link } = unlinkTarget;
    try {
      await api.delete(`/admin/submissions/${row.id}/link/${encodeURIComponent(link.invoice_number)}`);
      removeLinkLocally(row.id, row.invoice_number, link.invoice_number);
      toast.success(`Unlinked ${row.invoice_number} ↔ ${link.invoice_number}`);
      setUnlinkTarget(null);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // Client-side sort of the already-filtered rows. Filters remain server-side; sorting composes on top.
  const groupLabel = me?.role === "staff_group_a" ? "Sider" : me?.role === "staff_group_b" ? "Bridal" : "";
  const [printJob, setPrintJob] = useState(null);
  const printInvoices = (list) => { if (list.length) setPrintJob(preparePrint(list, groupLabel)); };

  const sortedRows = useMemo(() => {
    const t = (s) => (s ? new Date(s).getTime() : null);
    // Delivered Invoices archive: ALWAYS sort most-recently-delivered
    // first, regardless of the sort dropdown. Falls back to
    // `updated_at` for legacy rows that don't yet have `delivered_at`,
    // and finally to submitted_at, so the ordering is stable even
    // during the backfill period.
    if (view === "delivered") {
      const arr = [...rows];
      arr.sort((a, b) => {
        if (q) {
          const r = searchRank(a, q) - searchRank(b, q);
          if (r) return r;
        }
        const av = t(a.delivered_at) || t(a.updated_at) || t(a.submitted_at) || 0;
        const bv = t(b.delivered_at) || t(b.updated_at) || t(b.submitted_at) || 0;
        return bv - av;
      });
      return arr;
    }
    // Applied search: relevance bucket first (invoice > name > mobile > other),
    // then the chosen sort (or server order) within each bucket.
    const rel = q ? (a, b) => searchRank(a, q) - searchRank(b, q) : () => 0;
    if (!sort) {
      if (!q) return rows;
      return [...rows].sort(rel);
    }
    const arr = [...rows];
    const invoiceNum = (inv) => {
      const m = String(inv || "").match(/\d+/);
      return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
    };
    // Missing values (null) always sink to the bottom regardless of direction.
    const dateCmp = (a, b, dir) => {
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;
      return (a - b) * dir;
    };
    const nameCmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
    const fulfRank = (f) => (f === "home_delivery" ? 0 : 1);
    arr.sort((a, b) => {
      const r = rel(a, b);
      if (r) return r;
      switch (sort) {
        case "invoice_asc":      return invoiceNum(a.invoice_number) - invoiceNum(b.invoice_number) || nameCmp(a.invoice_number, b.invoice_number);
        case "invoice_desc":     return invoiceNum(b.invoice_number) - invoiceNum(a.invoice_number) || nameCmp(b.invoice_number, a.invoice_number);
        case "delivery_asc":     return dateCmp(t(a.internal_delivery_date), t(b.internal_delivery_date),  1);
        case "delivery_desc":    return dateCmp(t(a.internal_delivery_date), t(b.internal_delivery_date), -1);
        case "pickup_asc":       return dateCmp(t(a.pickup_date), t(b.pickup_date),  1);
        case "pickup_desc":      return dateCmp(t(a.pickup_date), t(b.pickup_date), -1);
        case "submitted_desc":   return (t(b.submitted_at) || 0) - (t(a.submitted_at) || 0);
        case "submitted_asc":    return (t(a.submitted_at) || 0) - (t(b.submitted_at) || 0);
        case "name_asc":         return nameCmp(a.customer_name, b.customer_name);
        case "name_desc":        return nameCmp(b.customer_name, a.customer_name);
        case "status_asc":       return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
        case "status_desc":      return (STATUS_RANK[b.status] ?? 99) - (STATUS_RANK[a.status] ?? 99);
        case "fulfillment_asc":  return fulfRank(a.fulfillment_type) - fulfRank(b.fulfillment_type);
        case "fulfillment_desc": return fulfRank(b.fulfillment_type) - fulfRank(a.fulfillment_type);
        default: return 0;
      }
    });
    return arr;
  }, [rows, sort, view, q]);


  const cards = useMemo(
    () => [
      { key: "Total", value: stats.total || 0 },
      { key: "Pending", value: stats.by_status?.Pending || 0 },
      { key: "Ready for Dispatch", value: stats.by_status?.["Ready for Dispatch"] || 0 },
      { key: "Delivered", value: stats.by_status?.Delivered || 0 },
    ],
    [stats]
  );

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-ivory/85 backdrop-blur border-b border-line">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div
            className="flex flex-col gap-2 min-[430px]:flex-row min-[430px]:items-center
              min-[430px]:justify-between sm:flex-row"
          >
            {/* LEFT — brand + staff identity */}
            <div
              data-testid="admin-header-brand"
              className="flex min-w-0 items-center gap-1.5 sm:gap-3"
            >
              <Logo size="header" withMonogram={false} />
              <span className="hidden md:inline-block h-5 w-px bg-line/80" />
              {me.group === "A" && (
                <span
                  data-testid="group-badge"
                  className="inline-flex items-center gap-1.5 rounded-full border border-maroon/25
                    bg-maroon/[0.08] px-2.5 py-1 text-[10px] font-semibold uppercase
                    tracking-[0.22em] text-maroon whitespace-nowrap sm:px-3 sm:text-[11px]"
                  title="Sider staff panel"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-maroon" aria-hidden />
                  Sider
                </span>
              )}
              {me.group === "B" && (
                <span
                  data-testid="group-badge"
                  className="inline-flex items-center gap-1.5 rounded-full border
                    border-[#2E3A6B]/25 bg-[#EDF0F7] px-2.5 py-1 text-[10px] font-semibold uppercase
                    tracking-[0.22em] text-[#2E3A6B] whitespace-nowrap sm:px-3 sm:text-[11px]"
                  title="Bridal staff panel"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#2E3A6B]" aria-hidden />
                  Bridal
                </span>
              )}
            </div>

            {/* RIGHT — controls */}
            <div
              data-testid="admin-header-controls"
              className="flex self-end items-center gap-2 min-[430px]:self-auto sm:gap-3"
            >
              <span
                className="hidden max-w-[16rem] truncate text-xs text-muted2 lg:inline"
                title={me.email || email}
              >
                {me.email || email}
              </span>
              <button
                data-testid="show-qr-btn"
                onClick={() => {
                  setQrTab("store");
                  setQrModalOpen(true);
                }}
                className="btn-outline-maroon inline-flex h-11 w-11 items-center justify-center
                  gap-1.5 !p-0 text-xs sm:h-auto sm:w-auto sm:!px-3 sm:!py-2"
                title="Store QR Code"
                aria-label="Store QR Code"
              >
                <QrCode size={14} strokeWidth={1.6} />
                <span className="hidden sm:inline">Store QR</span>
              </button>
              <button
                data-testid="security-settings-btn"
                onClick={() => setView("security")}
                className={`btn-outline-maroon inline-flex h-11 w-11 items-center justify-center
                  gap-1.5 !p-0 text-xs sm:h-auto sm:w-auto sm:!px-3 sm:!py-2 ${view === "security" ? "!bg-maroon !text-ivory" : ""}`}
                title="Security Settings"
                aria-label="Security Settings"
              >
                <ShieldCheck size={14} strokeWidth={1.6} />
                <span className="hidden sm:inline">Security</span>
              </button>
              <button
                data-testid="active-sessions-btn"
                onClick={openSessions}
                className="btn-outline-maroon inline-flex h-11 w-11 items-center justify-center
                  gap-1.5 !p-0 text-xs sm:h-auto sm:w-auto sm:!px-3 sm:!py-2"
                title="Active Sessions"
                aria-label="Active Sessions"
              >
                <MonitorSmartphone size={14} strokeWidth={1.6} />
                <span className="hidden sm:inline">Sessions</span>
              </button>
              <button
                data-testid="logout-btn"
                onClick={() => setConfirmLogout(true)}
                className="btn-outline-maroon inline-flex h-11 w-11 items-center justify-center
                  gap-1.5 !p-0 text-xs sm:h-auto sm:w-auto sm:!px-4 sm:!py-2"
                aria-label="Sign out"
              >
                <LogOut size={14} strokeWidth={1.6} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          {cards.map((c) => (
            <div key={c.key} className="bg-white border border-line rounded-2xl p-4 sm:p-5">
              <p className="overline text-[9px] sm:text-[10px] leading-tight">{c.key}</p>
              <p className="font-serif font-light text-3xl sm:text-4xl text-ink mt-1.5 sm:mt-2">{c.value}</p>
            </div>
          ))}
        </div>

        <PosterModal
          open={posterModalOpen}
          onClose={() => setPosterModalOpen(false)}
          qrUrl={
            posterKind === "website"
              ? websiteReviewQrUrl
              : posterKind === "review"
                ? reviewQrUrl
                : qrUrl
          }
          kind={posterKind}
        />

        {/* Store QR code — opened via header button, modal */}
        {qrModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={() => setQrModalOpen(false)}
            data-testid="qr-modal"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 sm:p-7"
              onClick={(e) => e.stopPropagation()}
              data-testid="qr-section"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-2">
                  <QrCode className="text-maroon" size={16} strokeWidth={1.6} />
                  <p className="overline text-[10px]">
                    {isWebsiteReviewQr
                      ? "Website AI Review QR"
                      : isDirectReviewQr
                        ? "Google Review QR Code"
                        : "Store QR Code"}
                  </p>
                </div>
                <button
                  data-testid="qr-modal-close"
                  onClick={() => setQrModalOpen(false)}
                  className="h-9 w-9 rounded-full hover:bg-ivory text-muted2 hover:text-maroon flex items-center justify-center transition-colors"
                  aria-label="Close"
                >
                  <X size={18} strokeWidth={1.6} />
                </button>
              </div>
              <div
                className="mb-5 grid grid-cols-1 gap-2 min-[480px]:grid-cols-3"
                role="tablist"
                aria-label="QR code type"
              >
                <button
                  data-testid="store-qr-tab"
                  type="button"
                  role="tab"
                  aria-selected={qrTab === "store"}
                  onClick={() => setQrTab("store")}
                  className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                    qrTab === "store"
                      ? "border-maroon bg-maroon text-white"
                      : "border-line bg-ivory/60 text-ink hover:border-maroon/40"
                  }`}
                >
                  Store QR Code
                </button>
                <button
                  data-testid="review-qr-tab"
                  type="button"
                  role="tab"
                  aria-selected={qrTab === "review"}
                  onClick={() => setQrTab("review")}
                  className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                    qrTab === "review"
                      ? "border-maroon bg-maroon text-white"
                      : "border-line bg-ivory/60 text-ink hover:border-maroon/40"
                  }`}
                >
                  Google Review QR
                </button>
                <button
                  data-testid="website-review-qr-tab"
                  type="button"
                  role="tab"
                  aria-selected={isWebsiteReviewQr}
                  onClick={() => setQrTab("website")}
                  className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                    isWebsiteReviewQr
                      ? "border-maroon bg-maroon text-white"
                      : "border-line bg-ivory/60 text-ink hover:border-maroon/40"
                  }`}
                >
                  Website AI Review QR
                </button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex-shrink-0 flex items-center justify-center">
                  <div className="rounded-2xl border border-line bg-ivory/60 p-3">
                    {activeQrReady ? (
                      <img
                        data-testid={
                          isWebsiteReviewQr
                            ? "website-review-qr-preview"
                            : isDirectReviewQr
                              ? "review-qr-preview"
                              : "qr-preview"
                        }
                        src={`${activeQrEndpoint}?format=svg&size=10`}
                        alt={`QR code linking to ${activeQrUrl}`}
                        className="w-40 h-40 sm:w-44 sm:h-44 block"
                      />
                    ) : (
                      <div className="w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center text-muted2">
                        <Loader2 className="animate-spin" size={20} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-xl sm:text-2xl text-ink">
                    {isWebsiteReviewQr
                      ? "Start the website AI review flow"
                      : isDirectReviewQr
                        ? "Collect Google Reviews in store"
                        : "Print on invoices & thank-you cards"}
                  </p>
                  <p
                    className="text-xs text-muted2 mt-1.5 break-all"
                    data-testid={
                      isWebsiteReviewQr
                        ? "website-review-qr-target-url"
                        : isDirectReviewQr
                          ? "review-qr-target-url"
                          : "qr-target-url"
                    }
                  >
                    Encodes: <span className="text-ink">{activeQrUrl || "—"}</span>
                  </p>
                  {activeQrError && (
                    <p data-testid="active-qr-error" className="mt-2 text-xs text-red-700">
                      The verified QR destination is unavailable. Please contact an administrator.
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      data-testid={
                        isWebsiteReviewQr
                          ? "download-website-review-qr-png"
                          : isDirectReviewQr
                            ? "download-review-qr-png"
                            : "download-qr-png"
                      }
                      onClick={() => downloadQr("png", qrTab)}
                      disabled={qrTab !== "store" && !activeQrReady}
                      className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Download size={13} strokeWidth={1.7} /> Download PNG
                    </button>
                    <button
                      data-testid={
                        isWebsiteReviewQr
                          ? "download-website-review-qr-svg"
                          : isDirectReviewQr
                            ? "download-review-qr-svg"
                            : "download-qr-svg"
                      }
                      onClick={() => downloadQr("svg", qrTab)}
                      disabled={qrTab !== "store" && !activeQrReady}
                      className="btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Download size={13} strokeWidth={1.7} /> Download SVG
                    </button>
                    <button
                      data-testid={
                        isWebsiteReviewQr
                          ? "open-website-review-poster-btn"
                          : isDirectReviewQr
                            ? "open-review-poster-btn"
                            : "open-poster-btn"
                      }
                      onClick={() => {
                        setPosterKind(qrTab);
                        setQrModalOpen(false);
                        setPosterModalOpen(true);
                      }}
                      disabled={qrTab !== "store" && !activeQrReady}
                      className="btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      title={
                        isWebsiteReviewQr
                          ? "Open the printable Website AI Review poster"
                          : isDirectReviewQr
                            ? "Open the printable Google Review poster"
                            : "Open the printable A5 welcome poster"
                      }
                    >
                      <Printer size={13} strokeWidth={1.7} /> Print A5 Poster
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backup Status panel — visible only to owner accounts on the
             BACKUP_ADMIN_EMAILS allowlist. Regular staff never see it. */}
        {isBackupAdmin && (
          <div className="mb-6 rounded-2xl border border-line bg-white p-5 sm:p-6" data-testid="backup-status-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-[#EFEAE1] p-2.5 text-[#8A6E43] flex-shrink-0">
                  <Database size={16} strokeWidth={1.8} />
                </div>
                <div>
                  <p className="overline text-[10px]">Disaster recovery</p>
                  <h3 className="font-serif text-xl text-ink mt-1">Backup Status</h3>
                  <p className="text-xs text-muted2 mt-1">
                    Stored in Emergent Object Storage · environment{" "}
                    <span className="text-ink font-medium">{backupStatus.environment}</span>
                    {" · "}retention {backupStatus.retention?.daily_days}d + {backupStatus.retention?.monthly_months}mo
                  </p>
                </div>
              </div>
              <button
                data-testid="backup-now-btn"
                onClick={runBackupNow}
                disabled={backupBusy}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {backupBusy ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} strokeWidth={1.7} />}
                Backup Now
              </button>
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl border border-line/70 p-3">
                <p className="overline text-[10px] mb-1">Last successful backup</p>
                {backupStatus.latest_success ? (
                  <>
                    <p className="text-ink font-medium" data-testid="backup-last-success">
                      {new Date(backupStatus.latest_success.started_at).toLocaleString("en-IN")}
                    </p>
                    <p className="text-[11px] text-muted2 mt-1">
                      {backupStatus.latest_success.collections?.reduce((a, c) => a + (c.count || 0), 0)} documents ·
                      {" "}{Math.round((backupStatus.latest_success.size_bytes || 0) / 1024)} KB ·
                      {" "}sha {backupStatus.latest_success.sha256?.slice(0, 8)}
                    </p>
                  </>
                ) : (
                  <p className="text-muted2 text-xs">No successful backup yet</p>
                )}
              </div>

              <div className="rounded-xl border border-line/70 p-3">
                <p className="overline text-[10px] mb-1">Backup status</p>
                {backupStatus.latest_run?.status === "success" ? (
                  <p className="inline-flex items-center gap-1.5 text-[#2D4A22] text-sm font-medium">
                    <ShieldCheck size={14} strokeWidth={1.9} /> Successful
                  </p>
                ) : backupStatus.latest_run?.status === "failed" ? (
                  <div>
                    <p className="inline-flex items-center gap-1.5 text-[#8A2A2A] text-sm font-medium">
                      <AlertTriangle size={14} strokeWidth={1.9} /> Backup failed — check settings
                    </p>
                    <p className="text-[11px] text-muted2 mt-1 break-all">
                      {backupStatus.latest_run.error?.slice(0, 120)}
                    </p>
                  </div>
                ) : backupStatus.latest_run?.status === "running" ? (
                  <p className="inline-flex items-center gap-1.5 text-muted2 text-sm">
                    <Loader2 className="animate-spin" size={14} /> Running…
                  </p>
                ) : (
                  <p className="text-muted2 text-xs">Awaiting first run</p>
                )}
              </div>

              <div className="rounded-xl border border-line/70 p-3">
                <p className="overline text-[10px] mb-1">Next scheduled backup</p>
                <p className="text-ink font-medium" data-testid="backup-next-scheduled">
                  {backupStatus.next_scheduled_utc
                    ? new Date(backupStatus.next_scheduled_utc).toLocaleString("en-IN")
                    : "—"}
                </p>
                <p className="text-[11px] text-muted2 mt-1">03:30 IST daily · driven by platform cron</p>
              </div>
            </div>
          </div>
        )}

        {me?.backup_admin && <ReviewLibraryPanel />}

        {/* View switcher: Active | Delivered | Recycle Bin */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 mb-3">
          <button
            data-testid="pre-register-btn"
            onClick={() => setPreRegOpen(true)}
            className="btn-outline-maroon !px-3.5 !py-2 sm:!py-1.5 !text-xs inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
            title="Pre-register an invoice ahead of customer form"
          >
            <QrCode size={13} strokeWidth={1.8} /> Pre-Register Invoice
          </button>
          <button
            data-testid="awaiting-btn"
            onClick={() => { setAwaitingOpen(true); loadAwaiting(); }}
            className="btn-outline-maroon !px-3.5 !py-2 sm:!py-1.5 !text-xs inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
            title="Show invoices awaiting customer submission"
            style={{ borderColor: "#B79E75", color: "#8A6E43" }}
          >
            <StickyNote size={13} strokeWidth={1.8} /> Awaiting Customer
          </button>
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="admin-view-tabs">
          <button
            data-testid="tab-active"
            onClick={() => setView("active")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] rounded-full transition-colors inline-flex items-center gap-1.5 ${
              view === "active"
                ? "bg-maroon text-ivory"
                : "bg-white text-muted2 border border-line hover:text-maroon"
            }`}
          >
            Active Submissions
            {(() => {
              const activeCount = Math.max(0, (stats.total || 0) - (stats.by_status?.Delivered || 0));
              return activeCount > 0 && view !== "active" && (
                <span className="ml-1 rounded-full bg-maroon/10 text-maroon text-[10px] px-1.5 py-0.5">
                  {activeCount}
                </span>
              );
            })()}
          </button>
          <button
            data-testid="tab-delivered"
            onClick={() => setView("delivered")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] rounded-full transition-colors inline-flex items-center gap-1.5 ${
              view === "delivered"
                ? "bg-maroon text-ivory"
                : "bg-white text-muted2 border border-line hover:text-maroon"
            }`}
          >
            <PackageCheck size={13} strokeWidth={1.7} />
            Delivered Invoices
            {(stats.by_status?.Delivered || 0) > 0 && view !== "delivered" && (
              <span data-testid="tab-delivered-count" className="ml-1 rounded-full bg-maroon/10 text-maroon text-[10px] px-1.5 py-0.5">
                {stats.by_status.Delivered}
              </span>
            )}
          </button>
          <button
            data-testid="tab-trash"
            onClick={() => setView("trash")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] rounded-full transition-colors inline-flex items-center gap-1.5 ${
              view === "trash"
                ? "bg-maroon text-ivory"
                : "bg-white text-muted2 border border-line hover:text-maroon"
            }`}
          >
            <Trash2 size={13} strokeWidth={1.7} />
            Recycle Bin
            {trashRows.length > 0 && view !== "trash" && (
              <span className="ml-1 rounded-full bg-maroon/10 text-maroon text-[10px] px-1.5 py-0.5">
                {trashRows.length}
              </span>
            )}
          </button>
          <button
            data-testid="tab-calendar"
            onClick={() => setView("calendar")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] rounded-full transition-colors inline-flex items-center gap-1.5 ${
              view === "calendar"
                ? "bg-maroon text-ivory"
                : "bg-white text-muted2 border border-line hover:text-maroon"
            }`}
          >
            <CalendarDays size={13} strokeWidth={1.7} />
            Delivery Calendar
          </button>
          <button
            data-testid="tab-register"
            onClick={() => setView("register")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] rounded-full transition-colors inline-flex items-center gap-1.5 ${
              view === "register"
                ? "bg-maroon text-ivory"
                : "bg-white text-muted2 border border-line hover:text-maroon"
            }`}
          >
            <ClipboardList size={13} strokeWidth={1.7} />
            Preparation Register
          </button>
        </div>

        <OtpDisabledBanner state={otpSetting.state} onRestore={otpSetting.setState} />

        {view === "delivered" && (
          <div
            data-testid="delivered-banner"
            className="mb-4 flex items-start gap-3 rounded-2xl border border-[#D6E9D5] bg-[#EDF6EA] px-4 py-3"
          >
            <PackageCheck size={16} strokeWidth={1.6} className="text-[#2D4A22] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-[#2D4A22] uppercase tracking-[0.18em]">Permanent archive</p>
              <p className="text-xs text-[#3D5A32] mt-1 leading-relaxed">
                Completed deliveries and pickups live here forever. Delete an invoice manually to send it to the Recycle Bin.
              </p>
            </div>
          </div>
        )}

        {view === "security" ? (
          <section data-testid="security-settings-view" className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <p className="overline text-[10px]">Settings · Global (Sider + Bridal)</p>
                <h2 className="font-serif text-xl text-ink mt-0.5 inline-flex items-center gap-2"><ShieldCheck size={18} strokeWidth={1.6} className="text-maroon" /> Security Settings</h2>
              </div>
              <button type="button" data-testid="security-back-btn" onClick={() => setView("active")} className="btn-outline-maroon !py-1.5 !px-3 !text-xs">Back</button>
            </div>
            <OtpVerificationSetting otp={otpSetting} showHistory />
          </section>
        ) : view === "register" ? (
          <PreparationRegister me={me} onOpenSubmission={openRegisterSubmission} />
        ) : view === "calendar" ? (
          <DeliveryCalendar
            me={me}
            onOpenInvoice={openCalendarInvoice}
            refreshKey={calendarRefreshKey}
          />
        ) : view === "active" || view === "delivered" ? (<>
        {/* Saved Filter Views — one-tap chips for the filter combos staff
            hit dozens of times a day. Only visible on the Active tab;
            the Delivered view is a permanent archive with its own logic. */}
        {view === "active" && (
          <div
            data-testid="saved-views-row"
            className="mb-4 -mx-1 px-1 overflow-x-auto no-scrollbar"
          >
            <div className="flex items-center gap-2 whitespace-nowrap py-1">
              <span className="overline text-[9px] flex-shrink-0 pr-1">Quick Views</span>
              <button
                type="button"
                data-testid="saved-view-all"
                onClick={clearAllFilters}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase transition-colors flex-shrink-0 border ${
                  isDefaultView
                    ? "bg-maroon text-ivory border-maroon"
                    : "bg-white text-muted2 border-line hover:text-maroon hover:border-maroon/50"
                }`}
              >
                All Active
              </button>
              {SAVED_VIEWS.map((v) => {
                const Icon = v.icon;
                const active = matchesView(v);
                return (
                  <button
                    key={v.key}
                    type="button"
                    data-testid={v.testId}
                    onClick={() => applyView(v)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase transition-colors flex-shrink-0 border ${
                      active
                        ? "bg-maroon text-ivory border-maroon"
                        : "bg-white text-muted2 border-line hover:text-maroon hover:border-maroon/50"
                    }`}
                    title={v.label}
                  >
                    <Icon size={12} strokeWidth={1.8} />
                    {v.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white border border-line rounded-2xl p-3 sm:p-5 mb-5 sm:mb-6">
          {/* Compact action row — Filter + Sort collapse by default. Export
              CSV / Excel + Refresh remain visible because they aren't
              filter/sort controls. */}
          {(() => {
            // Search text is deliberately NOT counted here — Search and
            // Filter are now separate controls (per Feb-2026 UI split).
            const activeFilterCount = [
              status !== "all",
              (dateFrom || "").trim() !== "",
              (dateTo || "").trim() !== "",
              fulfillment !== "all",
              (pickupDateFilter || "").trim() !== "",
              deliveryFilter !== "all",
            ].filter(Boolean).length;
            const activeSort = !!sort;
            const hasSearch = (q || "").trim() !== "";
            return (
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    data-testid="toggle-filter-panel"
                    onClick={() => { setFilterPanelOpen((v) => !v); setSearchPanelOpen(false); setSortPanelOpen(false); }}
                    aria-expanded={filterPanelOpen}
                    className={`btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5 relative ${filterPanelOpen ? "!bg-maroon !text-ivory" : ""}`}
                  >
                    <Filter size={13} strokeWidth={1.6} /> Filter
                    {activeFilterCount > 0 && (
                      <span
                        data-testid="active-filter-count"
                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-maroon text-ivory text-[10px] font-semibold px-1"
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  <button
                    data-testid="toggle-sort-panel"
                    onClick={() => { setSortPanelOpen((v) => !v); setSearchPanelOpen(false); setFilterPanelOpen(false); }}
                    aria-expanded={sortPanelOpen}
                    className={`btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5 ${sortPanelOpen ? "!bg-maroon !text-ivory" : ""}`}
                  >
                    <ArrowUpDown size={13} strokeWidth={1.6} /> Sort
                    {activeSort && (
                      <span
                        data-testid="active-sort-dot"
                        className="ml-1 inline-block w-2 h-2 rounded-full bg-maroon"
                      />
                    )}
                  </button>
                  <button
                    data-testid="toggle-search-panel"
                    onClick={() => { setSearchPanelOpen((v) => !v); setFilterPanelOpen(false); setSortPanelOpen(false); }}
                    aria-expanded={searchPanelOpen}
                    className={`btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5 ${searchPanelOpen ? "!bg-maroon !text-ivory" : ""}`}
                  >
                    <Search size={13} strokeWidth={1.6} /> Search
                    {hasSearch && (
                      <span
                        data-testid="active-search-dot"
                        className="ml-1 inline-block w-2 h-2 rounded-full bg-maroon"
                      />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    data-testid="export-csv-btn"
                    onClick={() => exportFile("csv")}
                    className="btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5"
                  >
                    <Download size={13} strokeWidth={1.6} /> CSV
                  </button>
                  <button
                    data-testid="export-xlsx-btn"
                    onClick={() => exportFile("xlsx")}
                    className="btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5"
                  >
                    <Download size={13} strokeWidth={1.6} /> Excel
                  </button>
                  <button
                    data-testid="admin-refresh-btn"
                    onClick={load}
                    className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1.5 transition-colors"
                  >
                    <RefreshCw size={13} strokeWidth={1.6} /> Refresh
                  </button>
                </div>
              </div>
            );
          })()}

          {filterPanelOpen && (
          <div data-testid="filter-panel" className="mt-4 pt-4 border-t border-line">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <div className="relative">
                <select
                  data-testid="admin-status-filter"
                  className="luxury-input !pr-11 appearance-none"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">All statuses</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <Filter className="absolute right-4 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" size={16} strokeWidth={1.5} />
              </div>
            </div>
            <div className="md:col-span-2">
              <DateFilterField
                testId="admin-date-from"
                value={dateFrom}
                onChange={setDateFrom}
                placeholder="From date"
              />
            </div>
            <div className="md:col-span-2">
              <DateFilterField
                testId="admin-date-to"
                value={dateTo}
                onChange={setDateTo}
                placeholder="To date"
              />
            </div>
            <div className="md:col-span-1 flex gap-2">
              <button data-testid="admin-apply-btn" onClick={load} className="btn-maroon !px-4 !py-2.5 !text-xs w-full">
                Apply
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center pt-4 border-t border-line">
            <div className="sm:col-span-2 flex items-center gap-2">
              <Filter size={14} strokeWidth={1.7} className="text-muted2 flex-shrink-0" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 whitespace-nowrap">Fulfillment</p>
            </div>
            <div className="sm:col-span-3">
              <select
                data-testid="fulfillment-filter"
                className="luxury-input appearance-none"
                value={fulfillment}
                onChange={(e) => setFulfillment(e.target.value)}
              >
                <option value="all">All</option>
                <option value="home_delivery">Home Delivery</option>
                <option value="store_pickup">Store Pickup</option>
              </select>
            </div>
            <div className="sm:col-span-3 flex items-center gap-2">
              <CalendarDays size={14} strokeWidth={1.7} className="text-muted2 flex-shrink-0" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 whitespace-nowrap">Pickup Date</p>
            </div>
            <div className="sm:col-span-4">
              <DateFilterField
                testId="pickup-date-filter"
                value={pickupDateFilter}
                onChange={setPickupDateFilter}
                placeholder="Select pickup date"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center pt-4 border-t border-line">
            <div className="sm:col-span-4 flex items-center gap-2">
              <CalendarDays size={14} strokeWidth={1.7} className="text-muted2 flex-shrink-0" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 whitespace-nowrap">Delivery Date</p>
            </div>
            <div className="sm:col-span-5">
              <select
                data-testid="delivery-filter"
                className="luxury-input appearance-none"
                value={deliveryFilter}
                onChange={(e) => setDeliveryFilter(e.target.value)}
              >
                <option value="all">All dates</option>
                <option value="unassigned">Not Assigned</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="week">This Week (next 7 days)</option>
                <option value="date">Custom Date…</option>
              </select>
            </div>
            <div className="sm:col-span-3">
              {deliveryFilter === "date" && (
                <DateFilterField
                  testId="delivery-custom-date"
                  value={deliveryCustomDate}
                  onChange={setDeliveryCustomDate}
                  placeholder="Select date"
                />
              )}
            </div>
          </div>
          </div>
          )}

          {sortPanelOpen && (
          <div data-testid="sort-panel" className="mt-4 pt-4 border-t border-line">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
            <div className="sm:col-span-2 flex items-center gap-2">
              <ArrowUpDown size={14} strokeWidth={1.7} className="text-muted2 flex-shrink-0" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 whitespace-nowrap">Sort By</p>
            </div>
            <div className="sm:col-span-10">
              <SortMenu value={sort} onChange={setSort} />
            </div>
          </div>
          </div>
          )}

          {searchPanelOpen && (
          <div data-testid="search-panel" className="mt-4 pt-4 border-t border-line">
            <form role="search" data-testid="admin-search-form" onSubmit={submitSearch} className="flex items-stretch gap-2">
              <div className="relative flex-1">
                <input
                  data-testid="admin-search-input"
                  type="search"
                  enterKeyHint="search"
                  autoFocus
                  autoComplete="off"
                  className="luxury-input !pl-11 !pr-11"
                  placeholder="Search invoice, name, mobile, PIN, city"
                  value={qDraft}
                  onChange={(e) => setQDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") setSearchPanelOpen(false); }}
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" size={16} strokeWidth={1.5} />
                {(qDraft || q) && (
                  <button
                    type="button"
                    data-testid="admin-search-clear"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted2 hover:text-maroon transition-colors p-1 rounded-full"
                    aria-label="Clear search"
                  >
                    <X size={14} strokeWidth={1.7} />
                  </button>
                )}
              </div>
              <button type="submit" data-testid="admin-search-submit" className="btn-maroon !px-4 !py-2 !text-xs whitespace-nowrap">Search</button>
            </form>
            <p className="mt-2 text-[11px] text-muted2 leading-relaxed">
              {q && qDraft.trim() !== q ? (
                <span data-testid="admin-search-pending">Showing results for <span className="text-ink">“{q}”</span> — press Enter or Search to apply the new text.</span>
              ) : q ? (
                <span data-testid="admin-search-applied">Showing results for <span className="text-ink">“{q}”</span>.</span>
              ) : (
                <>Type the full invoice number, then press Enter / ✓ or Search. Runs across the current tab — with <span className="text-ink">All statuses</span> selected in Filter, it spans Pending, Ready for Dispatch, and Delivered invoices.</>
              )}
            </p>
          </div>
          )}

          {/* Legacy sort-row placeholder removed by design (Filter/Sort now collapse) */}
        </div>

        {/* Table */}
        <div className="bg-white border border-line rounded-2xl overflow-hidden">
          {/* Mobile-only vertical card list — every invoice fits within one
              viewport frame so staff never swipe horizontally. All same data
              + handlers as the desktop table below; desktop layout is
              untouched (`hidden md:block` on the table container). */}
          <div className="md:hidden divide-y divide-line" data-testid="admin-mobile-cards">
            {loading ? (
              <div className="text-center py-16 text-muted2"><Loader2 className="mx-auto animate-spin" /></div>
            ) : sortedRows.length === 0 ? (
              <div className="text-center py-16 text-muted2">No submissions found.</div>
            ) : (
              sortedRows.map((r) => {
                const dupBadge =
                  r.dupatta_status === "collected" ? { text: "Dupatta Collected", cls: "bg-[#D6E9D5] text-[#2D4A22] border-[#2D4A22]/25", icon: CheckCircle2 } :
                  r.dupatta_status === "include"   ? { text: "Dupatta Pending",   cls: "bg-[#F5D3D3] text-[#8A2A2A] border-[#8A2A2A]/25", icon: AlertCircle } :
                  r.fulfillment_type === "store_pickup" ? { text: "In-Store Pickup", cls: "bg-[#DDE9F6] text-[#1F3A66] border-[#1F3A66]/25", icon: Store } :
                  null;
                const rowTintClass =
                  selected.has(r.id) ? "bg-maroon/[0.04]" :
                  r.dupatta_status === "collected" ? "bg-[#EDF6EA]" :
                  r.dupatta_status === "include"   ? "bg-[#FBECEC]" :
                  r.fulfillment_type === "store_pickup" ? "bg-[#EAF2FB]" :
                  "";
                return (
                  <div
                    key={r.id}
                    data-testid={`admin-mobile-card-${r.id}`}
                    className={`px-4 py-4 space-y-3 ${rowTintClass}`}
                  >
                    {/* Header — invoice # + status + select checkbox */}
                    <div className="flex items-start gap-3">
                      <button
                        data-testid={`m-row-select-${r.id}`}
                        onClick={() => toggleOne(r.id)}
                        className="text-maroon flex-shrink-0 mt-1"
                        aria-label={selected.has(r.id) ? `Deselect invoice ${r.invoice_number}` : `Select invoice ${r.invoice_number}`}
                      >
                        {selected.has(r.id) ? <CheckSquare size={16} strokeWidth={1.7} /> : <Square size={16} strokeWidth={1.7} />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p className="overline text-[10px] text-muted2 leading-none">INVOICE</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <p
                                data-testid={`m-invoice-number-${r.id}`}
                                className="font-serif text-4xl sm:text-5xl text-ink leading-none tracking-tight break-all"
                              >
                                {r.invoice_number}
                              </p>
                              {r.notes_count > 0 && (
                                <button
                                  type="button"
                                  data-testid={`m-note-indicator-${r.id}`}
                                  onClick={() => setDetail(r)}
                                  className="inline-flex items-center justify-center rounded-full bg-maroon/[0.08] border border-maroon/25 text-maroon w-7 h-7 hover:bg-maroon/[0.14] transition-colors flex-shrink-0"
                                  title="Internal note attached — tap to view"
                                  aria-label={`Invoice ${r.invoice_number} has an internal note`}
                                >
                                  <StickyNote size={13} strokeWidth={1.9} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <select
                              data-testid={`m-status-select-${r.id}`}
                              value={r.status}
                              onChange={(e) => updateStatus(r.id, e.target.value)}
                              className={`text-[11px] px-2.5 py-1 rounded-full border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-maroon/30 ${STATUS_COLORS[r.status] || "bg-gray-100"}`}
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {statusChangeLine(r) && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] text-muted2 tabular-nums whitespace-nowrap"
                                data-testid={`m-status-ts-${r.id}`}
                                title={statusChangeLine(r)}
                              >
                                <Clock size={10} strokeWidth={1.7} />
                                {statusChangeLine(r)}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-ink mt-2 break-words">{r.customer_name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[12px] text-muted2 tabular-nums">+91-{r.mobile}</p>
                          <MobileVerificationBadge status={r.mobile_verification} compact testId={`m-mobile-verification-${r.id}`} />
                          <button
                            type="button"
                            data-testid={`m-copy-mobile-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); copyMobileDigits(r); }}
                            className="text-muted2 hover:text-maroon transition-colors p-1.5 -m-1 inline-flex items-center justify-center rounded-full"
                            title="Copy 10-digit mobile number"
                            aria-label={`Copy mobile number for ${r.customer_name}`}
                          >
                            <Copy size={14} strokeWidth={1.7} />
                          </button>
                          <AttentionMarks row={r} onOpen={() => setDetail(r)} testPrefix="m" />
                        </div>
                        {Array.isArray(r.linked_invoices) && r.linked_invoices.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {r.linked_invoices.map((l) => (
                              <span
                                key={l.invoice_number}
                                data-testid={`m-linked-${r.id}-${l.invoice_number}`}
                                className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.06] text-maroon text-[10px] font-medium px-2 py-0.5 border border-maroon/15"
                                title={`Linked to ${l.invoice_number} (${l.group === "A" ? "Sider" : "Bridal"})`}
                              >
                                <Link2 size={9} strokeWidth={2.2} /> {l.invoice_number}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fulfilment + chargeable + city/pin + dupatta */}
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      {r.fulfillment_type === "store_pickup" ? (
                        <span data-testid={`m-fulfillment-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon font-medium px-2 py-0.5 border border-maroon/15">
                          <Store size={9} strokeWidth={2} /> Store Pickup
                        </span>
                      ) : (
                        <span data-testid={`m-fulfillment-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-white text-muted2 font-medium px-2 py-0.5 border border-line">
                          <Truck size={9} strokeWidth={2} /> Home Delivery
                        </span>
                      )}
                      {r.home_delivery_chargeable && r.fulfillment_type !== "store_pickup" && (
                        <span data-testid={`m-chargeable-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon font-semibold px-2 py-0.5 border border-maroon/25">
                          {rowChargeLabel(r)}
                        </span>
                      )}
                      {dupBadge && (
                        <span data-testid={`m-dupatta-badge-${r.id}`} className={`inline-flex items-center gap-1 rounded-full font-medium px-2 py-0.5 border ${dupBadge.cls}`}>
                          <dupBadge.icon size={10} strokeWidth={2} /> {dupBadge.text}
                        </span>
                      )}
                    </div>

                    <div className="text-[12px] text-muted2 flex items-center gap-1.5 break-words">
                      <MapPin size={11} strokeWidth={1.7} className="flex-shrink-0" />
                      <span className="min-w-0">
                        {r.address?.city || "—"} · {r.address?.pincode || "—"}
                      </span>
                    </div>

                    {/* Editable delivery date + salesman (Bridal only) */}
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] uppercase tracking-[0.18em] text-muted2">Delivery</span>
                        <DeliveryDateCell row={r} onChange={updateDeliveryDate} />
                      </div>
                      {isBridal && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] uppercase tracking-[0.18em] text-muted2">Salesman</span>
                          <SalesmanCell row={r} onChange={updateSalesman} />
                        </div>
                      )}
                      {r.fulfillment_type === "store_pickup" && r.pickup_date && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] uppercase tracking-[0.18em] text-muted2">Pickup</span>
                          <span className="text-[12px] text-ink">
                            {format(parseISO(r.pickup_date), "d MMM yyyy")}
                            {r.pickup_time_slot && <span className="text-muted2"> · {formatSlot(r.pickup_time_slot)}</span>}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <button
                        data-testid={`m-view-btn-${r.id}`}
                        onClick={() => setDetail(r)}
                        className="text-xs text-maroon inline-flex items-center gap-1 font-medium"
                      >
                        View More <ArrowUpRight size={11} strokeWidth={1.8} />
                      </button>
                      <div className="flex items-center gap-3.5">
                        <button
                          data-testid={`m-link-btn-${r.id}`}
                          onClick={() => setLinkRow(r)}
                          className="text-muted2 hover:text-maroon transition-colors"
                          aria-label={`Link a cross-group invoice to ${r.invoice_number}`}
                        >
                          <Link2 size={17} strokeWidth={1.7} />
                        </button>
                        <button
                          data-testid={`m-share-btn-${r.id}`}
                          onClick={() => shareInvoice(r)}
                          className="text-muted2 hover:text-maroon transition-colors"
                          aria-label={`Share fulfilment details for invoice ${r.invoice_number}`}
                        >
                          <Share2 size={17} strokeWidth={1.7} />
                        </button>
                        <button
                          data-testid={`m-print-btn-${r.id}`}
                          onClick={() => printInvoices([r])}
                          className="text-muted2 hover:text-maroon transition-colors"
                          aria-label={`Print fulfilment details for invoice ${r.invoice_number}`}
                        >
                          <Printer size={17} strokeWidth={1.7} />
                        </button>
                        <button
                          data-testid={`m-wa-btn-${r.id}`}
                          onClick={() => setWaRow(r)}
                          className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                          aria-label={`Send WhatsApp notification to the customer of invoice ${r.invoice_number}`}
                        >
                          <MessageCircle size={17} strokeWidth={1.9} />
                        </button>
                        <button
                          data-testid={`m-delete-btn-${r.id}`}
                          onClick={() => setConfirmDelete(r)}
                          className="text-muted2 hover:text-maroon transition-colors"
                          aria-label={`Delete submission for invoice ${r.invoice_number}`}
                        >
                          <Trash2 size={17} strokeWidth={1.7} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop / tablet table — unchanged. Hidden on mobile. */}
          <div className="hidden md:block overflow-x-auto -webkit-overflow-scrolling-touch">
            <table className="min-w-full text-sm">
              <thead className="bg-ivory/70 border-b border-line">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <button
                      data-testid="select-all-btn"
                      onClick={toggleAll}
                      className="text-maroon hover:text-maroon-hover transition-colors"
                      title={selected.size === sortedRows.length && sortedRows.length > 0 ? "Clear selection" : "Select all"}
                    >
                      {selected.size === sortedRows.length && sortedRows.length > 0 ? (
                        <CheckSquare size={16} strokeWidth={1.6} />
                      ) : (
                        <Square size={16} strokeWidth={1.6} />
                      )}
                    </button>
                  </th>
                  {(isBridal
                    ? ["Invoice", "Customer", "Fulfillment", "City / PIN", "Dupatta", "Submitted"]
                    : ["Invoice", "Customer", "Mobile", "Fulfillment", "City / PIN", "Dupatta", "Submitted"]
                  ).map((h) => (
                    <th key={h} className="text-left px-4 py-3 overline text-[10px] text-muted2">{h}</th>
                  ))}
                  <th className="text-left px-4 py-3 overline text-[10px] text-muted2 whitespace-nowrap">
                    <button
                      data-testid="sort-delivery-btn"
                      onClick={cycleSort}
                      className="inline-flex items-center gap-1.5 hover:text-maroon transition-colors"
                      title="Sort by delivery date"
                    >
                      Delivery Date
                      {sort === "delivery_asc" ? <ArrowUp size={11} strokeWidth={2} /> : sort === "delivery_desc" ? <ArrowDown size={11} strokeWidth={2} /> : <ArrowUpDown size={11} strokeWidth={1.6} />}
                    </button>
                  </th>
                  {isBridal && (
                    <th className="text-left px-4 py-3 overline text-[10px] text-muted2 whitespace-nowrap">Salesman</th>
                  )}
                  <th className="text-left px-4 py-3 overline text-[10px] text-muted2 whitespace-nowrap">
                    <button
                      data-testid="sort-pickup-btn"
                      onClick={() => setSort((s) => s === "pickup_asc" ? "pickup_desc" : s === "pickup_desc" ? "" : "pickup_asc")}
                      className="inline-flex items-center gap-1.5 hover:text-maroon transition-colors"
                      title="Sort by pickup date"
                    >
                      Pickup
                      {sort === "pickup_asc" ? <ArrowUp size={11} strokeWidth={2} /> : sort === "pickup_desc" ? <ArrowDown size={11} strokeWidth={2} /> : <ArrowUpDown size={11} strokeWidth={1.6} />}
                    </button>
                  </th>
                  {["Status", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-3 overline text-[10px] text-muted2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="admin-submissions-tbody">
                {loading ? (
                  <tr><td colSpan={12} className="text-center py-16 text-muted2"><Loader2 className="mx-auto animate-spin" /></td></tr>
                ) : sortedRows.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-16 text-muted2">No submissions found.</td></tr>
                ) : (
                  sortedRows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`row-${r.id}`}
                      className={`border-b border-line/60 transition-colors ${
                        selected.has(r.id)
                          ? "bg-maroon/[0.04]"
                          : r.dupatta_status === "collected"
                          ? "bg-[#EDF6EA] hover:bg-[#E4F0DF]"
                          : r.dupatta_status === "include"
                          ? "bg-[#FBECEC] hover:bg-[#F6DEDE]"
                          : r.fulfillment_type === "store_pickup"
                          ? "bg-[#EAF2FB] hover:bg-[#DEEAF6]"
                          : "hover:bg-ivory/50"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <button
                          data-testid={`row-select-${r.id}`}
                          onClick={() => toggleOne(r.id)}
                          className="text-maroon hover:text-maroon-hover transition-colors"
                        >
                          {selected.has(r.id) ? (
                            <CheckSquare size={16} strokeWidth={1.6} />
                          ) : (
                            <Square size={16} strokeWidth={1.6} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 font-medium text-ink">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{r.invoice_number}</span>
                          <AttentionMarks row={r} onOpen={() => setDetail(r)} testPrefix="d" />
                          {r.notes_count > 0 && (
                            <span
                              data-testid={`notes-badge-${r.id}`}
                              title={`${r.notes_count} internal note${r.notes_count === 1 ? "" : "s"}`}
                              className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon text-[10px] font-medium px-2 py-0.5 border border-maroon/15"
                            >
                              <StickyNote size={10} strokeWidth={1.8} />
                              {r.notes_count}
                            </span>
                          )}
                          {Array.isArray(r.linked_invoices) && r.linked_invoices.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap" data-testid={`linked-badges-${r.id}`}>
                              {r.linked_invoices.map((l) => (
                                <span
                                  key={l.invoice_number}
                                  data-testid={`linked-badge-${r.id}-${l.invoice_number}`}
                                  title={`Linked to invoice ${l.invoice_number} (${l.group_label}). Click the unlink icon to remove.`}
                                  className="inline-flex items-center gap-1 rounded-full bg-[#EFEAE1] text-[#5A4626] text-[10px] font-medium px-2 py-0.5 border border-[#B79E75]/40"
                                >
                                  <Link2 size={10} strokeWidth={1.9} />
                                  {l.invoice_number} · {l.group_label}
                                  <button
                                    data-testid={`unlink-btn-${r.id}-${l.invoice_number}`}
                                    onClick={(e) => { e.stopPropagation(); setUnlinkTarget({ row: r, link: l }); }}
                                    className="ml-0.5 -mr-0.5 text-[#8A6E43] hover:text-maroon transition-colors"
                                    aria-label={`Unlink invoice ${l.invoice_number} from ${r.invoice_number}`}
                                    title="Unlink"
                                  >
                                    <X size={10} strokeWidth={2.2} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <span>{r.customer_name}</span>
                          {isBridal && (
                            <MobileVerificationBadge status={r.mobile_verification} compact testId={`mobile-verification-${r.id}`} />
                          )}
                        </div>
                      </td>
                      {!isBridal && (
                        <td className="px-4 py-4 text-muted2">
                          <div className="flex flex-col gap-1">
                          <div className="inline-flex items-center gap-2">
                            <span className="tabular-nums">+91-{r.mobile}</span>
                            <button
                              type="button"
                              data-testid={`copy-mobile-${r.id}`}
                              onClick={(e) => { e.stopPropagation(); copyMobileDigits(r); }}
                              className="text-muted2/70 hover:text-maroon transition-colors p-1 -m-1 rounded-full"
                              title="Copy 10-digit mobile number"
                              aria-label={`Copy mobile number for ${r.customer_name}`}
                            >
                              <Copy size={14} strokeWidth={1.7} />
                            </button>
                          </div>
                          <MobileVerificationBadge status={r.mobile_verification} compact testId={`mobile-verification-${r.id}`} />
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-4">
                        {r.fulfillment_type === "store_pickup" ? (
                          <span data-testid={`fulfillment-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon text-[10px] font-medium px-2 py-0.5 border border-maroon/15">
                            <Store size={9} strokeWidth={2} /> Store Pickup
                          </span>
                        ) : (
                          <span data-testid={`fulfillment-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-ivory text-muted2 text-[10px] font-medium px-2 py-0.5 border border-line">
                            <Truck size={9} strokeWidth={2} /> Home Delivery
                          </span>
                        )}
                        {r.home_delivery_chargeable && r.fulfillment_type !== "store_pickup" && (
                          <span
                            data-testid={`chargeable-badge-${r.id}`}
                            title={`Home delivery is chargeable (${rowChargeLabel(r)})`}
                            className="ml-1 inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon text-[10px] font-semibold px-2 py-0.5 border border-maroon/25"
                          >
                            {rowChargeLabel(r)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-muted2">{r.address?.city || "—"} · {r.address?.pincode || "—"}</td>
                      <td className="px-4 py-4 text-xs">
                        {r.dupatta_status === "collected" ? (
                          <span
                            data-testid={`dupatta-badge-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#D6E9D5] text-[#2D4A22] text-[10px] font-medium px-2 py-0.5 border border-[#2D4A22]/25"
                          >
                            <CheckCircle2 size={10} strokeWidth={2} /> Dupatta Collected
                          </span>
                        ) : r.dupatta_status === "include" ? (
                          <span
                            data-testid={`dupatta-badge-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#F5D3D3] text-[#8A2A2A] text-[10px] font-medium px-2 py-0.5 border border-[#8A2A2A]/25"
                          >
                            <AlertCircle size={10} strokeWidth={2} /> Dupatta Pending
                          </span>
                        ) : r.fulfillment_type === "store_pickup" ? (
                          <span
                            data-testid={`dupatta-badge-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#DDE9F6] text-[#1F3A66] text-[10px] font-medium px-2 py-0.5 border border-[#1F3A66]/25"
                          >
                            <Store size={10} strokeWidth={2} /> In-Store Pickup
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs text-muted2">{new Date(r.submitted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <DeliveryDateCell row={r} onChange={updateDeliveryDate} />
                      </td>
                      {isBridal && (
                        <td className="px-4 py-4 whitespace-nowrap">
                          <SalesmanCell row={r} onChange={updateSalesman} />
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-xs text-muted2">
                        {r.fulfillment_type === "store_pickup" && r.pickup_date ? (
                          <div>
                            <div className="text-ink">{format(parseISO(r.pickup_date), "d MMM yyyy")}</div>
                            <div className="text-[10px]">{formatSlot(r.pickup_time_slot)}</div>
                          </div>
                        ) : (
                          <span className="italic">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <select
                          data-testid={`status-select-${r.id}`}
                          value={r.status}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          className={`text-xs px-3 py-1.5 rounded-full border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-maroon/30 ${STATUS_COLORS[r.status] || "bg-gray-100"}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            data-testid={`view-btn-${r.id}`}
                            onClick={() => setDetail(r)}
                            className="text-xs text-maroon hover:underline"
                          >
                            View
                          </button>
                          <button
                            data-testid={`link-btn-${r.id}`}
                            onClick={() => setLinkRow(r)}
                            className="text-muted2 hover:text-maroon transition-colors"
                            title={`Link an invoice from the ${/^1\d{3}$/.test(r.invoice_number) ? "Bridal" : "Sider"} group`}
                            aria-label={`Link a cross-group invoice to ${r.invoice_number}`}
                          >
                            <Link2 size={15} strokeWidth={1.7} />
                          </button>
                          <button
                            data-testid={`share-btn-${r.id}`}
                            onClick={() => shareInvoice(r)}
                            className="text-muted2 hover:text-maroon transition-colors"
                            title="Share fulfilment details"
                            aria-label={`Share fulfilment details for invoice ${r.invoice_number}`}
                          >
                            <Share2 size={15} strokeWidth={1.7} />
                          </button>
                          <button
                            data-testid={`print-btn-${r.id}`}
                            onClick={() => printInvoices([r])}
                            className="text-muted2 hover:text-maroon transition-colors"
                            title="Print fulfilment details"
                            aria-label={`Print fulfilment details for invoice ${r.invoice_number}`}
                          >
                            <Printer size={15} strokeWidth={1.7} />
                          </button>
                          <button
                            data-testid={`wa-btn-${r.id}`}
                            onClick={() => setWaRow(r)}
                            className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                            title="Send WhatsApp notification to customer"
                            aria-label={`Send WhatsApp notification to the customer of invoice ${r.invoice_number}`}
                          >
                            <MessageCircle size={15} strokeWidth={1.9} />
                          </button>
                          <button
                            data-testid={`delete-btn-${r.id}`}
                            onClick={() => setConfirmDelete(r)}
                            className="text-muted2 hover:text-maroon transition-colors"
                            title="Move to Recycle Bin"
                            aria-label={`Delete submission for invoice ${r.invoice_number}`}
                          >
                            <Trash2 size={15} strokeWidth={1.6} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>) : (
          /* Recycle Bin view */
          <div className="bg-white border border-line rounded-2xl overflow-hidden" data-testid="recycle-bin-view">
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-line bg-ivory/60">
              <div>
                <p className="overline text-[10px]">Recycle Bin</p>
                <p className="font-serif text-lg text-ink mt-0.5">
                  Deleted submissions · auto-purged after 60 days
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="trash-refresh-btn"
                  onClick={loadTrash}
                  className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1.5 transition-colors"
                >
                  <RefreshCw size={13} strokeWidth={1.6} /> Refresh
                </button>
                <button
                  data-testid="empty-trash-btn"
                  onClick={() => setConfirmEmpty(true)}
                  disabled={trashRows.length === 0}
                  className="btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <AlertTriangle size={13} strokeWidth={1.7} /> Empty Recycle Bin
                </button>
              </div>
            </div>
            {/* Mobile-only card list — single-frame view, no horizontal
                swipe. Same data + handlers as the desktop table below;
                desktop layout (`hidden md:block`) is unchanged. */}
            <div className="md:hidden divide-y divide-line" data-testid="trash-mobile-cards">
              {trashLoading ? (
                <div className="text-center py-16 text-muted2"><Loader2 className="mx-auto animate-spin" /></div>
              ) : trashRows.length === 0 ? (
                <div className="text-center py-16 text-muted2">The Recycle Bin is empty.</div>
              ) : (
                trashRows.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`trash-mobile-card-${r.id}`}
                    className="px-4 py-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-lg text-ink leading-tight">
                          <span className="overline text-[9px] text-muted2 mr-1.5">INVOICE</span>
                          {r.invoice_number}
                        </p>
                        <p className="text-sm text-ink mt-1 break-words">{r.customer_name}</p>
                        <p className="text-[12px] text-muted2 tabular-nums">+91-{r.mobile}</p>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium flex-shrink-0 ${r.days_remaining <= 7 ? "bg-[#F1DCDC] text-[#8A2A2A]" : "bg-ivory text-muted2 border border-line"}`}
                        title="Days until auto-purge"
                      >
                        Purges in {r.days_remaining}d
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <p className="overline text-[9px] text-muted2">Submitted</p>
                        <p className="text-ink mt-0.5">
                          {new Date(r.submitted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </p>
                      </div>
                      <div>
                        <p className="overline text-[9px] text-muted2">Deleted</p>
                        <p className="text-ink mt-0.5">
                          {r.deleted_at ? new Date(r.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </p>
                      </div>
                      {r.deleted_by && (
                        <div className="col-span-2">
                          <p className="overline text-[9px] text-muted2">Deleted by</p>
                          <p className="text-ink mt-0.5 break-all text-[12px]">{r.deleted_by}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pt-1">
                      <button
                        data-testid={`m-restore-btn-${r.id}`}
                        onClick={() => restoreSubmission(r)}
                        className="text-xs text-maroon inline-flex items-center gap-1.5 font-medium justify-center border border-maroon/25 rounded-full px-3 py-2 hover:bg-maroon/[0.04] transition-colors"
                      >
                        <RotateCcw size={12} strokeWidth={1.7} /> Restore
                      </button>
                      <button
                        data-testid={`m-purge-btn-${r.id}`}
                        onClick={() => setConfirmPurge(r)}
                        className="text-xs text-[#8A2A2A] inline-flex items-center gap-1.5 font-medium justify-center border border-[#8A2A2A]/25 rounded-full px-3 py-2 hover:bg-[#8A2A2A]/[0.04] transition-colors"
                        title="Permanently delete this invoice — cannot be restored"
                      >
                        <Trash2 size={12} strokeWidth={1.7} /> Delete Permanently
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop / tablet table — unchanged. Hidden on mobile. */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-ivory/70 border-b border-line">
                  <tr>
                    {["Invoice", "Customer", "Mobile", "Submitted", "Deleted", "Deleted by", "Purges in", ""].map((h) => (
                      <th key={h} className="text-left px-4 py-3 overline text-[10px] text-muted2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="trash-tbody">
                  {trashLoading ? (
                    <tr><td colSpan={8} className="text-center py-16 text-muted2"><Loader2 className="mx-auto animate-spin" /></td></tr>
                  ) : trashRows.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-16 text-muted2">The Recycle Bin is empty.</td></tr>
                  ) : (
                    trashRows.map((r) => (
                      <tr key={r.id} className="border-b border-line/60 hover:bg-ivory/50 transition-colors" data-testid={`trash-row-${r.id}`}>
                        <td className="px-4 py-4 font-medium text-ink">{r.invoice_number}</td>
                        <td className="px-4 py-4">{r.customer_name}</td>
                        <td className="px-4 py-4 text-muted2">+91-{r.mobile}</td>
                        <td className="px-4 py-4 text-xs text-muted2">{new Date(r.submitted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                        <td className="px-4 py-4 text-xs text-muted2">{r.deleted_at ? new Date(r.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                        <td className="px-4 py-4 text-xs text-muted2">{r.deleted_by || "—"}</td>
                        <td className="px-4 py-4 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${r.days_remaining <= 7 ? "bg-[#F1DCDC] text-[#8A2A2A]" : "bg-ivory text-muted2 border border-line"}`}>
                            {r.days_remaining} day{r.days_remaining === 1 ? "" : "s"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <button
                              data-testid={`restore-btn-${r.id}`}
                              onClick={() => restoreSubmission(r)}
                              className="text-xs text-maroon hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                            >
                              <RotateCcw size={12} strokeWidth={1.7} /> Restore
                            </button>
                            <button
                              data-testid={`purge-btn-${r.id}`}
                              onClick={() => setConfirmPurge(r)}
                              className="text-xs text-[#8A2A2A] hover:text-[#6A1F2B] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                              title="Permanently delete this invoice — cannot be restored"
                            >
                              <Trash2 size={12} strokeWidth={1.7} /> Delete Permanently
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 w-[min(96vw,720px)]"
        >
          <div className="bg-ink text-ivory rounded-3xl sm:rounded-full shadow-[0_20px_60px_-20px_rgba(44,26,29,0.45)] border border-maroon/20 px-3 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 pr-2 sm:pr-3 sm:border-r border-white/15">
              <CheckSquare size={16} strokeWidth={1.6} className="text-maroon" />
              <span data-testid="bulk-count" className="text-sm font-medium whitespace-nowrap">
                {selected.size} selected
              </span>
            </div>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.24em] text-ivory/60">Move to</span>
            <select
              data-testid="bulk-status-select"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="flex-1 min-w-[140px] sm:flex-none bg-white/10 text-ivory text-sm rounded-full px-3 sm:px-4 py-2 border border-white/15 focus:outline-none focus:ring-2 focus:ring-maroon/40 cursor-pointer"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="text-ink">{s}</option>
              ))}
            </select>
            <button
              data-testid="bulk-apply-btn"
              onClick={bulkUpdate}
              disabled={bulkBusy}
              className="bg-maroon hover:bg-maroon-hover text-ivory rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
            >
              {bulkBusy ? <Loader2 className="animate-spin" size={14} /> : <ArrowUpRight size={14} strokeWidth={1.8} />}
              Apply
            </button>
            <button
              data-testid="bulk-clear-btn"
              onClick={() => setSelected(new Set())}
              className="text-ivory/60 hover:text-ivory transition-colors"
              title="Clear selection"
            >
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>
          <div className="mt-2 flex justify-center gap-2 flex-wrap">
            <button
              data-testid="bulk-share-worklist-btn"
              onClick={shareSelectedWorkList}
              title="Share one consolidated work list (invoice, salesman, dupatta) via WhatsApp"
              className="bg-[#25D366] text-white rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 shadow-[0_10px_30px_-12px_rgba(44,26,29,0.35)] hover:brightness-95 transition-[filter]"
            >
              <Share2 size={14} strokeWidth={1.9} />
              Share Selected ({selected.size})
            </button>
            <button
              data-testid="bulk-share-details-btn"
              onClick={() => shareInvoicesBulk(sortedRows.filter((r) => selected.has(r.id)))}
              title="Share the full fulfilment details of every selected invoice as one message"
              className="bg-white text-ink rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 shadow-[0_10px_30px_-12px_rgba(44,26,29,0.35)] hover:bg-ivory transition-colors"
            >
              <Share2 size={14} strokeWidth={1.9} className="text-maroon" />
              Bulk Share Details ({selected.size})
            </button>
            <button
              data-testid="bulk-print-btn"
              onClick={() => printInvoices(sortedRows.filter((r) => selected.has(r.id)))}
              title="Print the full fulfilment details of every selected invoice, one invoice per page"
              className="bg-white text-ink rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 shadow-[0_10px_30px_-12px_rgba(44,26,29,0.35)] hover:bg-ivory transition-colors"
            >
              <Printer size={14} strokeWidth={1.9} className="text-maroon" />
              Print ({selected.size})
            </button>
          </div>
        </div>
      )}

      {/* Bulk WhatsApp — composer / queue / summary */}
      <PrintConfirmModal job={printJob} onClose={() => setPrintJob(null)} />
      <BulkWhatsAppModal
        state={bulkWaOpen}
        queue={bulkWaQueue}
        update={bulkWaUpdate}
        setUpdate={setBulkWaUpdate}
        idx={bulkWaIdx}
        previewIdx={bulkWaPreviewIdx}
        setPreviewIdx={setBulkWaPreviewIdx}
        sent={bulkWaSent}
        skipped={bulkWaSkipped}
        close={closeBulkWa}
        startQueue={startBulkWaQueue}
        markSent={bulkWaMarkSent}
        markSkipped={bulkWaMarkSkipped}
        openChat={bulkWaOpenChat}
      />

      {/* Bundled confirmation dialogs — see admin/ConfirmDialogs.jsx.
          Includes: delete (→ Recycle Bin), permanent purge, empty
          Recycle Bin (typed "EMPTY" gate), logout confirmation, and
          the one-time WhatsApp Business-only acknowledgment. */}
      <ConfirmDialogs
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        deleteSubmission={deleteSubmission}
        confirmPurge={confirmPurge}
        setConfirmPurge={setConfirmPurge}
        purgeBusy={purgeBusy}
        permanentlyDelete={permanentlyDelete}
        confirmEmpty={confirmEmpty}
        setConfirmEmpty={setConfirmEmpty}
        emptyConfirmText={emptyConfirmText}
        setEmptyConfirmText={setEmptyConfirmText}
        emptyRecycleBin={emptyRecycleBin}
        trashRows={trashRows}
        confirmLogout={confirmLogout}
        setConfirmLogout={setConfirmLogout}
        logoutBusy={logoutBusy}
        logout={logout}
        waBizGate={waBizGate}
        setWaBizGate={setWaBizGate}
        confirmWaBizGate={confirmWaBizGate}
      />

      {/* Link Invoice modal — cross-group linking */}
      {linkRow && (
        <LinkInvoiceModal
          row={linkRow}
          onClose={() => setLinkRow(null)}
          onLinked={(preview) => {
            applyLinkLocally(linkRow.id, linkRow.invoice_number, preview);
            setLinkRow(null);
          }}
        />
      )}

      {/* WhatsApp customer notify — confirm preview before opening chat */}
      {waRow && (
        <WhatsAppNotifyModal
          row={waRow}
          onClose={() => setWaRow(null)}
          guardedOpenWhatsApp={guardedOpenWhatsApp}
        />
      )}

      {/* Active Sessions / Logged-in Devices + revoke confirmations */}
      <SessionsModals
        sessionsOpen={sessionsOpen}
        setSessionsOpen={setSessionsOpen}
        sessionsLoading={sessionsLoading}
        sessionsList={sessionsList}
        sessionsBusy={sessionsBusy}
        loadSessions={loadSessions}
        confirmRevokeSession={confirmRevokeSession}
        setConfirmRevokeSession={setConfirmRevokeSession}
        confirmRevokeAll={confirmRevokeAll}
        setConfirmRevokeAll={setConfirmRevokeAll}
        revokeSession={revokeSession}
        revokeAllSessions={revokeAllSessions}
      />



      {/* Logout confirmation moved into admin/ConfirmDialogs.jsx (Feb 2026 refactor). */}

      {/* Business-only WhatsApp acknowledgment also lives inside
          admin/ConfirmDialogs.jsx now — same behaviour, same wording,
          same one-time-per-browser localStorage gate. */}


      {/* Pre-Register Invoice modal */}
      {/* Pre-Register Invoice + Awaiting Customer bundle — see
          admin/PreRegisterModals.jsx. State + submit/cancel handlers
          still live in this file; the extracted component is a
          controlled view driven entirely by props. */}
      <PreRegisterModals
        preRegOpen={preRegOpen}
        setPreRegOpen={setPreRegOpen}
        preRegForm={preRegForm}
        setPreRegForm={setPreRegForm}
        preRegBusy={preRegBusy}
        submitPreReg={submitPreReg}
        preRegDateOpen={preRegDateOpen}
        setPreRegDateOpen={setPreRegDateOpen}
        isBridal={isBridal}
        awaitingOpen={awaitingOpen}
        setAwaitingOpen={setAwaitingOpen}
        awaitingRows={awaitingRows}
        cancelPreReg={cancelPreReg}
        toggleChargeable={toggleChargeable}
      />

      {/* Unlink confirmation */}
      {unlinkTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => setUnlinkTarget(null)}
          data-testid="confirm-unlink-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#EFEAE1] p-2.5 text-[#8A6E43] flex-shrink-0">
                <Link2Off size={18} strokeWidth={1.7} />
              </div>
              <div>
                <p className="overline text-[10px]">Unlink invoice</p>
                <h3 className="font-serif text-xl text-ink mt-1">
                  Unlink {unlinkTarget.row.invoice_number} ↔ {unlinkTarget.link.invoice_number}?
                </h3>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">
                  The link between{" "}
                  <span className="text-ink font-medium">{unlinkTarget.row.invoice_number}</span>{" "}
                  and{" "}
                  <span className="text-ink font-medium">
                    {unlinkTarget.link.invoice_number} · {unlinkTarget.link.group_label}
                  </span>{" "}
                  will be removed from both invoices. Neither invoice is
                  modified in any other way — payments, delivery dates,
                  dupatta status and workflows remain independent.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                data-testid="confirm-unlink-cancel"
                onClick={() => setUnlinkTarget(null)}
                className="btn-outline-maroon !px-4 !py-2 !text-xs"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-unlink-confirm"
                onClick={unlinkInvoice}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5"
              >
                <Link2Off size={13} strokeWidth={1.7} /> Unlink
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      <DetailDrawer
        detail={detail}
        onClose={() => setDetail(null)}
        onDetailChange={setDetail}
        onRowPatch={(id, patch) =>
          setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)))
        }
        isBridal={isBridal}
        isAdmin={me?.role === "admin"}
        notes={notes}
        noteText={noteText}
        setNoteText={setNoteText}
        noteBusy={noteBusy}
        onAddNote={addNote}
        onUpdateStatus={updateStatus}
        onToggleChargeable={toggleChargeable}
        onUnlinkStart={setUnlinkTarget}
      />
    </div>
  );
}


/**
 * Compact WhatsApp notification status shown at the top of the admin detail
 * drawer. Fetches `/admin/submissions/:id/notifications` on open and renders
 * one small pill per notification type. Never fatal — a fetch error simply
 * hides the block.
 */

/**
 * Cross-group invoice linking modal. Staff enters a target invoice
 * number, the backend confirms it exists in the OPPOSITE group and
 * returns minimal customer info so staff can visually verify before
 * confirming. Uses only the `/admin/invoice-preview` + `/admin/link`
 * endpoints — group-security is enforced backend-side.
 */

/**
 * WhatsApp customer-notify confirmation modal. Renders a preview of the
 * generated message, blocks the "Open WhatsApp" button when the invoice
 * has no delivery date / pickup date, is already Delivered, or has an
 * invalid mobile — and opens `https://wa.me/91<digits>?text=...` in a
 * new tab so the staff member sends manually.
 */

function WhatsAppNotifyModal({ row, onClose, guardedOpenWhatsApp }) {
  const { text, canSend, reason } = React.useMemo(() => buildCustomerWhatsAppMessage(row), [row]);
  const digits = String(row.mobile || "").replace(/\D/g, "").slice(-10);
  const mobileFmt = digits.length === 10 ? `+91 ${digits}` : "—";

  const openWhatsApp = () => {
    if (!canSend) return;
    const url = whatsappDeepLink(row, text);
    // `noopener,noreferrer` so the WhatsApp window can't reach back into this
    // tab. The Business-only ack gate wraps this call so staff sees the
    // "Use WhatsApp Business" reminder before any customer chat is opened.
    guardedOpenWhatsApp(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      onClose();
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="whatsapp-notify-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[#DCF8C6] p-2.5 text-[#128C7E] flex-shrink-0">
            <MessageCircle size={18} strokeWidth={1.9} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="overline text-[10px]">Customer notification</p>
            <h3 className="font-serif text-xl text-ink mt-1">Send WhatsApp Notification?</h3>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <p className="overline text-[10px] mb-0.5">To</p>
                <p className="text-ink font-medium truncate" data-testid="wa-modal-customer">{row.customer_name || "—"}</p>
                <p className="text-muted2 text-[11px]" data-testid="wa-modal-mobile">{mobileFmt}</p>
              </div>
              <div>
                <p className="overline text-[10px] mb-0.5">Invoice</p>
                <p className="text-ink font-medium" data-testid="wa-modal-invoice">{row.invoice_number}</p>
                <p className="text-muted2 text-[11px]">
                  {row.fulfillment_type === "store_pickup" ? "In-Store Pickup" : "Home Delivery"}
                </p>
              </div>
              <div>
                <p className="overline text-[10px] mb-0.5">{row.fulfillment_type === "store_pickup" ? "Pickup" : "Delivery"}</p>
                <p className="text-ink font-medium">
                  {(() => {
                    const d = row.fulfillment_type === "store_pickup" ? row.pickup_date : row.internal_delivery_date;
                    if (!d) return "—";
                    try { return format(parseISO(d), "d MMM yyyy"); } catch { return d; }
                  })()}
                </p>
              </div>
            </div>

            {canSend ? (
              <div className="mt-4">
                <p className="overline text-[10px] mb-1.5">Message preview</p>
                <div
                  data-testid="wa-message-preview"
                  className="rounded-xl bg-[#F8F5EE] border border-line/70 px-3 py-3 text-[12.5px] text-ink whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto"
                >
                  {text}
                </div>
                <div className="mt-2.5 rounded-xl bg-[#25D366]/[0.08] border border-[#128C7E]/25 px-3 py-2 flex items-start gap-2">
                  <ShieldCheck size={13} strokeWidth={1.9} className="text-[#128C7E] flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed text-ink">
                    <span className="font-medium">Business only.</span>{" "}
                    <span className="text-muted2">
                      Always send from Tanish Textiles WhatsApp Business — never personal WhatsApp.
                      If your device shows a chooser, tap <span className="text-ink font-medium">WhatsApp Business</span>.
                    </span>
                  </p>
                </div>
                <p className="text-[11px] text-muted2 mt-1.5">
                  WhatsApp will open with this message pre-filled. You'll still need to press Send.
                </p>
              </div>
            ) : (
              <div
                className="mt-4 text-xs bg-[#FBECEC] border border-[#8A2A2A]/20 text-[#8A2A2A] rounded-lg px-3 py-2.5"
                data-testid="wa-blocked-reason"
              >
                {reason}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            data-testid="wa-cancel-btn"
            onClick={onClose}
            className="btn-outline-maroon !px-4 !py-2 !text-xs"
          >
            Cancel
          </button>
          <button
            data-testid="wa-open-btn"
            onClick={openWhatsApp}
            disabled={!canSend}
            className="!px-4 !py-2 !text-xs inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#128C7E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MessageCircle size={13} strokeWidth={2.1} /> Open WhatsApp Business
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Cross-group invoice linking modal. Staff enters a target invoice
 * number, the backend confirms it exists in the OPPOSITE group and
 * returns minimal customer info so staff can visually verify before
 * confirming. Uses only the `/admin/invoice-preview` + `/admin/link`
 * endpoints — group-security is enforced backend-side.
 */
function LinkInvoiceModal({ row, onClose, onLinked }) {
  const sourceGroup = /^1\d{3}$/.test(row.invoice_number) ? "A" : "B";
  const otherLabel = sourceGroup === "A" ? "Bridal" : "Sider";
  const otherHint = sourceGroup === "A" ? "2000-2999" : "1000-1999";
  const [query, setQuery] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState("");
  const [searching, setSearching] = React.useState(false);
  const [linking, setLinking] = React.useState(false);

  const alreadyLinked = React.useMemo(() => {
    const links = Array.isArray(row.linked_invoices) ? row.linked_invoices : [];
    return new Set(links.map((l) => l.invoice_number));
  }, [row]);

  const search = async () => {
    const inv = query.trim();
    setError("");
    setPreview(null);
    if (!inv) { setError("Please enter an invoice number."); return; }
    if (inv === row.invoice_number) { setError("You cannot link an invoice to itself."); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/admin/invoice-preview", { params: { invoice_number: inv } });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not find that invoice in the other group.");
    } finally {
      setSearching(false);
    }
  };

  const link = async () => {
    if (!preview) return;
    setLinking(true);
    try {
      await api.post(`/admin/submissions/${row.id}/link`, { target_invoice_number: preview.invoice_number });
      toast.success(`Linked ${row.invoice_number} ↔ ${preview.invoice_number} · ${preview.group_label}`);
      onLinked(preview);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not link the invoices.");
    } finally {
      setLinking(false);
    }
  };

  const isAlready = preview && alreadyLinked.has(preview.invoice_number);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="link-invoice-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[#EFEAE1] p-2.5 text-[#8A6E43] flex-shrink-0">
            <Link2 size={18} strokeWidth={1.7} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="overline text-[10px]">Cross-group link</p>
            <h3 className="font-serif text-xl text-ink mt-1">
              Link a {otherLabel} invoice
            </h3>
            <p className="text-sm text-muted2 mt-2 leading-relaxed">
              You are linking a companion invoice to{" "}
              <span className="text-ink font-medium">{row.invoice_number}</span>{" "}
              ({sourceGroup === "A" ? "Sider" : "Bridal"}). The two invoices stay separate — payments,
              delivery dates and dupatta status remain independent.
            </p>

            <div className="mt-4">
              <label className="overline text-[10px] block mb-1">
                {otherLabel} invoice number
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="link-invoice-input"
                  className="luxury-input !py-2 flex-1"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPreview(null); setError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                  placeholder={`e.g. ${sourceGroup === "A" ? "2002" : "1001"}`}
                  autoFocus
                />
                <button
                  data-testid="link-search-btn"
                  onClick={search}
                  disabled={searching}
                  className="btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {searching ? <Loader2 className="animate-spin" size={13} /> : <Search size={13} strokeWidth={1.7} />}
                  Search
                </button>
              </div>
              <p className="text-[11px] text-muted2 mt-1.5">
                Only {otherLabel} invoices ({otherHint}) can be linked from here.
              </p>
            </div>

            {error && (
              <div className="mt-3 text-xs bg-[#FBECEC] border border-[#8A2A2A]/20 text-[#8A2A2A] rounded-lg px-3 py-2" data-testid="link-invoice-error">
                {error}
              </div>
            )}

            {preview && (
              <div className="mt-4 border border-line rounded-xl p-3 bg-ivory/50" data-testid="link-invoice-preview">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">
                    Invoice {preview.invoice_number}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#EFEAE1] text-[#5A4626] text-[10px] font-medium px-2 py-0.5 border border-[#B79E75]/40">
                    {preview.group_label}
                  </span>
                </div>
                <div className="mt-2 text-[11px] text-muted2">
                  Invoice exists in {preview.group_label}. Customer details are
                  hidden across groups — confirm with your counterpart before linking.
                </div>
                {isAlready && (
                  <div className="mt-2 text-[11px] text-[#8A6E43] bg-[#EFEAE1]/70 border border-[#B79E75]/40 rounded-md px-2 py-1">
                    This invoice is already linked to {row.invoice_number}.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            data-testid="link-cancel-btn"
            onClick={onClose}
            className="btn-outline-maroon !px-4 !py-2 !text-xs"
          >
            Cancel
          </button>
          <button
            data-testid="link-confirm-btn"
            onClick={link}
            disabled={!preview || linking || isAlready}
            className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {linking ? <Loader2 className="animate-spin" size={13} /> : <Link2 size={13} strokeWidth={1.8} />}
            {isAlready ? "Already Linked" : "Link Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

